namespace Solovis.Reports.ReportHelpers.IntervalStats {


  /**
   * IntervalStatsHolderMap maps intervalCode and fullLabelPath to the set of data associated with calculations for that time period and line item.
   * This will be used by custom column functions to retrieve data specific to certain  
   */
  export class IntervalStatsHolderMap {

    private _keyToHolderMap;
    private _groupNodeIdToStatsByAssetMap;
    private _fullLabelPathToStatsByAssetMap;

    constructor() {
      this._keyToHolderMap = {};
      this._groupNodeIdToStatsByAssetMap = {};
      this._fullLabelPathToStatsByAssetMap = {};
    }

    private static createKey(intervalCode, fullLabelPath) {
      return "$" + intervalCode + "$" + fullLabelPath;
    }

    public addIntervalStatsHolders(statsByAssetEntry) {
      // statsByAssetEntry is an item from IntervalStats' loadedDataSet.statsByAsset.

      let fullLabelPath;

      if (statsByAssetEntry && statsByAssetEntry.compositeInfo) {
        fullLabelPath = statsByAssetEntry.compositeInfo.fullLabelPath;

        this._fullLabelPathToStatsByAssetMap[statsByAssetEntry.compositeInfo.fullLabelPath] = statsByAssetEntry;
        if (Solovis.isNotNull(statsByAssetEntry.compositeInfo.groupNodeId)) {
          this._groupNodeIdToStatsByAssetMap[statsByAssetEntry.compositeInfo.groupNodeId] = statsByAssetEntry;
        }
      }
      else if (statsByAssetEntry && statsByAssetEntry.assetInfo) {
        fullLabelPath = statsByAssetEntry.assetInfo.label;
      }

      if (fullLabelPath) {
        if (statsByAssetEntry.byInterval) {
          for (let intervalStats of statsByAssetEntry.byInterval) {
            let key = IntervalStatsHolderMap.createKey(intervalStats.intervalCode, fullLabelPath);
            this._keyToHolderMap[key] = intervalStats;
          }
        }
      }
    }

    public getIntervalStatsHolder(intervalCode, fullLabelPath) {
      let key = IntervalStatsHolderMap.createKey(intervalCode, fullLabelPath);

      return this._keyToHolderMap[key];
    }

    public tryGetStatsByAssetEntryForNodeId(nodeId) {
      return this._groupNodeIdToStatsByAssetMap[nodeId];
    }

    public tryGetStatsByAssetEntryForFullLabelPath(fullLabelPath) {
      return this._fullLabelPathToStatsByAssetMap[fullLabelPath];
    }
  }

  export class IntervalStatsReportGenerator {

    public static generateReportDefinition() {

      let statsFieldLabels = {
        "cumulativeReturn": "Cumulative Return",
        "annualizedReturn": "Annualized Return",
        "standardDeviation": "Monthly Standard Deviation",
        "annualizedStandardDeviation": "Annualized Standard Deviation",
        "variance": "Monthly Variance",
        "downsideStandardDeviation": "Monthly Downside Standard Deviation",
        "mean": "Monthly Arithmetic Mean",
        "sampleSkewness": "Monthly Sample Skewness",
        "sampleExcessKurtosis": "Monthly Sample Excess Kurtosis",

        "cumulativeIrr": "Cumulative IRR",
        "annualizedIrr": "Annualized IRR",
        "endOfDayCumulativeIrr": "EoD Cumulative IRR",
        "endOfDayAnnualizedIrr": "EoD Annualized IRR",

        "correlation": "Monthly Correlation",
        "covariance": "Monthly Covariance",
        "deltaMean": "Monthly Delta Mean",
        "deltaStandardDeviation": "Monthly Delta Standard Deviation",
        "downsideDeltaStandardDeviation": "Monthly Downside Delta Standard Deviation"
      };

      let sequenceStatsFieldLabels = {
        "cumulativeReturn": "Cumulative Return",
        "annualizedReturn": "Annualized Return",
        "standardDeviation": "Standard Deviation",
        "annualizedStandardDeviation": "Annualized Standard Deviation",
        "variance": "Variance",
        "downsideStandardDeviation": "Downside Standard Deviation",
        "mean": "Arithmetic Mean",
        "sampleSkewness": "Sample Skewness",
        "sampleExcessKurtosis": "Sample Excess Kurtosis",

        "correlation": "Correlation",
        "covariance": "Covariance",
        "deltaMean": "Delta Mean",
        "deltaStandardDeviation": "Delta Standard Deviation",
        "downsideDeltaStandardDeviation": "Downside Delta Standard Deviation"
      };

      let statsFieldDataTypes = {
        "cumulativeReturn": "percentage",
        "annualizedReturn": "percentage",
        "standardDeviation": "percentage",
        "annualizedStandardDeviation": "percentage",
        "variance": "number",
        "downsideStandardDeviation": "percentage",
        "mean": "percentage",
        "sampleSkewness": "number",
        "sampleExcessKurtosis": "number",

        "cumulativeIrr": "percentage",
        "annualizedIrr": "percentage",
        "endOfDayCumulativeIrr": "percentage",
        "endOfDayAnnualizedIrr": "percentage",

        "correlation": "number",
        "covariance": "number",
        "deltaMean": "percentage",
        "deltaStandardDeviation": "percentage",
        "downsideDeltaStandardDeviation": "percentage"
      };

      let statsFieldHelp = {
        "cumulativeReturn": { url: "~/Help/InvestmentModelHelp/CalculationsReference#cumulative_return" },
        "annualizedReturn": { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_return" },
        "standardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_standard_deviation" },
        "annualizedStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_standard_deviation" },
        "variance": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_variance" },
        "downsideStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_downside_standard_deviation" },

        "cumulativeIrr": { url: "~/Help/InvestmentModelHelp/CalculationsReference#cumulative_irr" },
        "annualizedIrr": { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_irr" },

        "endOfDayCumulativeIrr": { url: "~/Help/InvestmentModelHelp/CalculationsReference#eod_cumulative_irr" },
        "endOfDayAnnualizedIrr": { url: "~/Help/InvestmentModelHelp/CalculationsReference#eod_annualized_irr" },

        "mean": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_arithmetic_mean" },
        "sampleSkewness": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_sample_skewness" },
        "sampleExcessKurtosis": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_sample_excess_kurtosis" },
        "correlation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_correlation" },
        "covariance": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_covariance" },

        "deltaMean": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_delta_mean" },
        "deltaStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_delta_standard_deviation" },
        "downsideDeltaStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_downside_delta_standard_deviation" },
      };

      let otherHelp = {
        "attributionResidualEffect": { url: "~/Help/InvestmentModelHelp/CalculationsReference_Attribution#residual_effect" }
      };

      let assetModes = {
        Assets: "Assets",
        Composite: "Composite"
      };

      let isSyntheticComponentLineItem = function (lineItem) {
        if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
          return true;
        }

        return false;
      };

      let isGroupLineItem = function (lineItem) {
        if (lineItem.compositeInfo && lineItem.compositeInfo.isGroup) {
          return true;
        }

        return false;
      };

      let cellContextMenuItems = function (cellContext) {
        let inspectionInfo = Solovis.Reports.DataSetHelpers.IntervalStatsInspectionHelper.getLineItemInspectionInfoForIntervalStatsCellContext(cellContext);
        if (inspectionInfo) {
          return inspectionInfo.cellContextMenuItems;
        }
      };

      let createErrorIndicatorSpan = function (errorMessage) {
        let errorSpan = $("<span class='fa fa-exclamation-triangle'>")
          .css("color", "#f00")
          .css("padding-left", "5px")
          .attr("title", errorMessage);
        return errorSpan;
      };

      let createAssetLabelFieldGetter = function (tableControl: Solovis.Reports.Parts.Tables.DataTableControl) {
        let allLabelToUse = "All";

        let useCompositeSelectionSetLabelForAll: boolean;
        let otherOptions: Solovis.Reports.Parameters.Stats.StatsOtherOptions = tableControl.parameters.otherOptions;
        if (!otherOptions) {
          otherOptions = {};
        }

        if (otherOptions.useCompositeSelectionSetLabelForAll === "Yes") {
          useCompositeSelectionSetLabelForAll = true;
        }
        else if (otherOptions.useCompositeSelectionSetLabelForAll === "No") {
          useCompositeSelectionSetLabelForAll = false;
        }
        else {
          let siteReportSettings = tableControl.reportControl.getSiteReportSettings();
          useCompositeSelectionSetLabelForAll = (siteReportSettings && siteReportSettings.custom) ? !!siteReportSettings.custom.useCompositeSelectionSetLabelForAll : false;
        }

        if (useCompositeSelectionSetLabelForAll) {
          let intervalStats = tableControl.dataSets.intervalStats;
          if (intervalStats.compositeAllLabel) {
            allLabelToUse = intervalStats.compositeAllLabel;
          }
          else {
            let compositeSelectionSet = tableControl.parameters.compositeSelectionSet;
            if (compositeSelectionSet && compositeSelectionSet.name) {
              allLabelToUse = compositeSelectionSet.name;
            }
          }
        }

        return function (row) {
          if (row.compositeInfo && row.compositeInfo.fullLabelPath === "All") {
            return allLabelToUse;
          }
          if (row.assetInfo) {
            if (row.assetInfo.label) {
              return row.assetInfo.label;
            }
          }
        }
      };

      let getLineMetadataHolder = function (lineItem) {
        // Retrieve object that has resourceId/entityId/investmentId/etc.                
        if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
          return lineItem.compositeInfo.underlyingComponentMetadata;
        }
        else {
          return lineItem.assetInfo;
        }
      };

      let lineResourceIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.resourceId : null;
      };

      let lineEntityIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.entityId : null;
      };

      let lineInvestmentIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.investmentId : null;
      };

      let lineHoldingResourceIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.holdingResourceId : null;
      };

      let lineHoldingIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.holdingId : null;
      };

      let lineOwnerResourceIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.ownerResourceId : null;
      };

      let lineOwnerIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.ownerId : null;
      };

      let lineTrueOwnerResourceIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.trueOwnerResourceId : null;
      };

      let lineTrueOwnerIdGetter = function (row) {
        let metadataHolder = getLineMetadataHolder(row);
        return metadataHolder ? metadataHolder.trueOwnerId : null;
      };

      let assetInfoResourceIdFormatter = function (value, valueContext) {
        let assetLabel = value;
        if (!assetLabel) {
          return null;
        }

        let row = valueContext.row;

        let includeAssetLinks = false;

        let assetSpan = $("<span>");

        let assetRef: any = {};

        if (!isGroupLineItem(row)) {
          if (isSyntheticComponentLineItem(row)) {
            assetRef.resourceId = Solovis.resolveObject("compositeInfo.underlyingComponentMetadata.resourceId", row);
          }
          else {
            assetRef.resourceId = Solovis.resolveObject("assetInfo.resourceId", row);
          }

          if (assetRef.resourceId) {
            includeAssetLinks = true;
          }
        }

        if (includeAssetLinks) {
          let assetLink = $("<a>").appendTo(assetSpan);
          assetLink.text(assetLabel);

          Solovis.UI.PopupLinkPanel.onElementClick(assetLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: cellContextMenuItems(valueContext)
            });
          });
        }
        else {
          assetSpan.text(assetLabel);
        }

        if (row.errorMessage) {
          createErrorIndicatorSpan(row.errorMessage).appendTo(assetSpan);
        }

        return assetSpan;
      };

      let dataSourceForIntervalStats = {
        name: "intervalStats",
        path: "/Solovis/Performance/IntervalStats",

        dependsOn: [
          { name: "selectionSetContents", ref: { elementName: "Root", dataSourceName: "selectionSetContents" } }
        ],


        parameterMap: function (parameters, more) {
          let params: any = {};

          IntervalStatsReportGenerator.validateRequiredStatSetOptions(parameters.statSet);

          params.sourceDataRef = {};
          for (var k in parameters.sourceData) params.sourceDataRef[k] = parameters.sourceData[k];          

          if (parameters.assetMode === assetModes.Assets) {
            let assetResourceIdsSeen = {};
            let assetResourceIds = [];

            $(parameters.assets).each(function (i, asset: any) {
              if (!assetResourceIdsSeen[asset.resourceId]) {
                assetResourceIdsSeen[asset.resourceId] = true;
                assetResourceIds.push(asset.resourceId);
              }
            });

            if (parameters.assetSelectionSet) {
              let selectionSetContents = more.dataSets.selectionSetContents;

              $(selectionSetContents.resourceIds).each(function (i, resourceId: any) {
                if (!assetResourceIdsSeen[resourceId]) {
                  assetResourceIdsSeen[resourceId] = true;
                  assetResourceIds.push(resourceId);
                }
              });
            }

            params.assetResourceIds = assetResourceIds;

            if (params.sourceDataRef.compositesUseChildrensTwrDates !== null && params.sourceDataRef.compositesUseChildrensTwrDates) {
              params.sourceDataRef.compositesUseChildrensTwrDates = false;
            }
          }
          else if (parameters.assetMode === assetModes.Composite) {
            let compositeSelectionSet = parameters.compositeSelectionSet;
            if (!compositeSelectionSet) {
              return null;
            }
            params.compositeSelectionSetRef = {
              id: compositeSelectionSet.id,
              custom: compositeSelectionSet.custom
            };
          }

          params.statSetOptions = parameters.statSet;

          if (params.statSetOptions && params.statSetOptions.basicStats && (params.statSetOptions.basicStats.irrVariations || params.statSetOptions.basicStats.twrVariations)) {

            let customEnvironmentUserData = new Solovis.Portal.SitePortalServices().customEnvironmentUserData();
            if (customEnvironmentUserData && customEnvironmentUserData.ModelSettings) {

              let modelSettings = customEnvironmentUserData.ModelSettings;
              if (modelSettings.performanceVariationsConfig && modelSettings.performanceVariationsConfig.variations) {

                var variations = modelSettings.performanceVariationsConfig.variations;

                if (params.statSetOptions.basicStats.irrVariations) {
                  params.statSetOptions.basicStats.irrVariations = _.intersection(
                    params.statSetOptions.basicStats.irrVariations,
                    variations.filter(v => v.allowForIrr).map(v => v.code));
                }

                if (params.statSetOptions.basicStats.twrVariations) {
                  params.statSetOptions.basicStats.twrVariations = _.intersection(
                    params.statSetOptions.basicStats.twrVariations,
                    variations.filter(v => v.allowForTwr).map(v => v.code));
                }
              }
            }
          }

          let to = parameters.to ? parameters.to : InvData.DayId.today();

          if (parameters.intervalMode === "IntervalSet") {
            let intervalSetResult = InvData.Util.IntervalSetGenerator.getIntervalSet(parameters.intervalSet, to);
            params.intervalRequests = intervalSetResult.intervalRequests;
          }
          else {
            let from = parameters.from;

            params.intervalRequests = [
              { code: "Interval", fromDayId: from, toDayId: to }
            ];
          }

          if (parameters.targetCurrency) {
            params.targetCurrencyId = parameters.targetCurrency.id;
          }

          if (parameters.otherOptions && parameters.otherOptions.outputFilter) {
            params.outputFilter = parameters.otherOptions.outputFilter;
          }

          return params;
        },

        postProcess: function (loadedDataSet, more) {
          let lineItems = [];

          let intervalStatsHolderMap = new IntervalStatsHolderMap();

          let to = more.originalParameters.to ? more.originalParameters.to : InvData.DayId.today();
          let sequences = more.originalParameters.sequences;

          let intervalSetResult = null;
          if (more.originalParameters.intervalMode === "IntervalSet") {
            intervalSetResult = InvData.Util.IntervalSetGenerator.getIntervalSet(more.originalParameters.intervalSet, to);
          }
          else {
            intervalSetResult = {
              codeToIntervalInfoMap: {}
            };

            $(loadedDataSet.intervalRequests).each(function (i, intervalRequest: any) {
              let intervalInfo = {
                intervalRequest: intervalRequest
              };
              intervalSetResult.codeToIntervalInfoMap[intervalRequest.code] = intervalInfo;
            });
          }

          let getBaseLineItemForAsset = function (asset) {
            let lineItem: any = {
              assetInfo: asset.assetInfo,
              compositeInfo: asset.compositeInfo
            };

            lineItem.basicStatsBenchmark = asset.basicStatsBenchmark;
            lineItem.betaAlphaMarket = asset.betaAlphaMarket;
            lineItem.sortinoRatioTargetReturn = asset.sortinoRatioTargetReturn;
            lineItem.sharpeRatioBenchmark = asset.sharpeRatioBenchmark;
            lineItem.annualizedSharpeRatio = asset.annualizedSharpeRatio;
            lineItem.informationRatioBenchmark = asset.informationRatioBenchmark;
            lineItem.upsideDownsideCaptureRatioBenchmark = asset.upsideDownsideCaptureRatioBenchmark;
            lineItem.pmeBenchmark = asset.pmeBenchmark;

            return lineItem;
          };

          let copyCalcDetails = function (lineItem, assetInterval) {
            lineItem.errorMessage = assetInterval.errorMessage;

            lineItem.intervalCode = assetInterval.intervalCode;
            let intervalInfo = intervalSetResult.codeToIntervalInfoMap[lineItem.intervalCode];
            lineItem.intervalLabel = intervalInfo.label;
            lineItem.intervalTimeRangeDescription = intervalInfo.timeRangeDescription;
            lineItem.intervalFromDayId = intervalInfo.intervalRequest.fromDayId;
            lineItem.intervalToDayId = intervalInfo.intervalRequest.toDayId;

            lineItem.twrFromDayIdUsed = assetInterval.twrFromDayIdUsed;
            lineItem.twrToDayIdUsed = assetInterval.twrToDayIdUsed;
            lineItem.txBasedFromDayIdUsed = assetInterval.txBasedFromDayIdUsed;
            lineItem.txBasedToDayIdUsed = assetInterval.txBasedToDayIdUsed;

            lineItem.valuesResult = assetInterval.valuesResult;
            if (lineItem.valuesResult && lineItem.valuesResult.twrVariations) {
              lineItem.valuesResult.twrVariationByCode = {};
              $(lineItem.valuesResult.twrVariations).each(function (i, variation: any) {
                lineItem.valuesResult.twrVariationByCode[variation.code] = variation;
              });
            }

            lineItem.bySequence = assetInterval.bySequence;
            lineItem.basicStats = assetInterval.basicStats;
            lineItem.gainLossSummary = assetInterval.gainLossSummary;
            lineItem.maxDrawdownResult = assetInterval.maxDrawdownResult;
            lineItem.sincePeakPerformanceResult = assetInterval.sincePeakPerformanceResult;
            lineItem.betaAlphaResult = assetInterval.betaAlphaResult;
            lineItem.sortinoRatioResult = assetInterval.sortinoRatioResult;
            lineItem.sharpeRatioResult = assetInterval.sharpeRatioResult;
            lineItem.informationRatioResult = assetInterval.informationRatioResult;
            lineItem.upsideDownsideCaptureRatioResult = assetInterval.upsideDownsideCaptureRatioResult;
            lineItem.pmeResult = assetInterval.pmeResult;
            lineItem.compositeResult = assetInterval.compositeResult;
            lineItem.attributionResult = assetInterval.attributionResult;
            lineItem.txResult = assetInterval.txResult;
            lineItem.drawdownResult = assetInterval.drawdownResult;

            if (InvData.Util.DateIndexHelper.isValidDateIndex(assetInterval.blendedValuesCutoffDayId)) {
              lineItem.blendedValuesCutoffDayId = assetInterval.blendedValuesCutoffDayId;
            }
            if (lineItem.basicStats && lineItem.basicStats.irrVariations) {
              lineItem.irrVariationByCode = {};
              $(lineItem.basicStats.irrVariations).each(function (i, variation: any) {
                lineItem.irrVariationByCode[variation.code] = variation;
              });
            }

            if (lineItem.basicStats && lineItem.basicStats.twrVariations) {
              lineItem.twrVariationByCode = {};
              $(lineItem.basicStats.twrVariations).each(function (i, variation: any) {
                lineItem.twrVariationByCode[variation.code] = variation;
              });
            }

            if (lineItem.txResult && lineItem.txResult.customTxGroups) {
              lineItem.customTxGroupByCode = {};
              $(lineItem.txResult.customTxGroups).each(function (i, customTxGroup: any) {
                lineItem.customTxGroupByCode[customTxGroup.groupCode] = customTxGroup;
              });
            }

            if (lineItem.attributionResult && lineItem.attributionResult.byTier) {
              lineItem.attributionByTier = {};
              $(lineItem.attributionResult.byTier).each(function (i, attributionForTier) {
                lineItem.attributionByTier["tier" + i] = attributionForTier;
              });
            }
          };

          let setupGroupNodeIds = function (lineItem) {
            if (lineItem.compositeInfo) {
              let intervalCodePrefix = "";
              if (lineItem.intervalCode) {
                intervalCodePrefix = lineItem.intervalCode + "__";
              }

              if (Solovis.isNotNull(lineItem.compositeInfo.groupNodeId)) {
                lineItem.fullGroupNodeId = intervalCodePrefix + lineItem.compositeInfo.groupNodeId;
              }
              if (Solovis.isNotNull(lineItem.compositeInfo.parentGroupNodeId)) {
                lineItem.fullParentGroupNodeId = intervalCodePrefix + lineItem.compositeInfo.parentGroupNodeId;
              }
            }
          };

          $(loadedDataSet.statsByAsset).each(function (i, asset: any) {
            intervalStatsHolderMap.addIntervalStatsHolders(asset);

            if (more.originalParameters.intervalMode === "IntervalSet") {
              if (!more.originalParameters.showIntervalsAsColumns) {
                $(asset.byInterval).each(function (i, assetInterval) {
                  let lineItem = getBaseLineItemForAsset(asset);

                  copyCalcDetails(lineItem, assetInterval);
                  setupGroupNodeIds(lineItem);

                  lineItems.push(lineItem);
                });
              }
              else {
                // Merge all interval data into a single line item.
                let lineItem = getBaseLineItemForAsset(asset);
                lineItem.byIntervalCode = {};

                let combinedErrorMessage = null;

                let minIntervalFromDayId = 9999999;
                let maxIntervalToDayId = 0;
                let blendedValuesCutoffDayId = null;

                $(asset.byInterval).each(function (i, assetInterval: any) {
                  let intervalStatsHolder: any = {};

                  copyCalcDetails(intervalStatsHolder, assetInterval);
                  setupGroupNodeIds(lineItem);

                  lineItem.byIntervalCode[assetInterval.intervalCode] = intervalStatsHolder;

                  if (intervalStatsHolder.intervalToDayId) {
                    maxIntervalToDayId = Math.max(maxIntervalToDayId, intervalStatsHolder.intervalToDayId);
                  }

                  if (intervalStatsHolder.intervalFromDayId) {
                    minIntervalFromDayId = Math.min(minIntervalFromDayId, intervalStatsHolder.intervalFromDayId);
                  }
                  else {
                    minIntervalFromDayId = 0;
                  }

                  if (InvData.Util.DateIndexHelper.isValidDateIndex(intervalStatsHolder.blendedValuesCutoffDayId)) {
                    blendedValuesCutoffDayId = intervalStatsHolder.blendedValuesCutoffDayId;
                  }
                  else {
                    blendedValuesCutoffDayId = null;
                  }

                  if (intervalStatsHolder.errorMessage) {
                    if (!combinedErrorMessage) {
                      combinedErrorMessage = intervalStatsHolder.errorMessage;
                    }
                    else {
                      // Tell user to view by interval to see other error messages.
                      combinedErrorMessage += " (...see other interval error messages)";
                    }
                  }
                });

                lineItem.minIntervalFromDayId = minIntervalFromDayId;
                lineItem.maxIntervalToDayId = maxIntervalToDayId;

                if (blendedValuesCutoffDayId !== null) {
                  lineItem.blendedValuesCutoffDayId = blendedValuesCutoffDayId;
                }

                lineItem.errorMessage = combinedErrorMessage;

                lineItems.push(lineItem);
              }
            }
            else {
              let lineItem = getBaseLineItemForAsset(asset);

              if (asset.byInterval && asset.byInterval.length > 0) {
                // There will only be one since we requested one interval.
                let assetInterval = asset.byInterval[0];
                copyCalcDetails(lineItem, assetInterval);
              }

              setupGroupNodeIds(lineItem);

              lineItems.push(lineItem);
            }
          });

          let dataSet = {
            lineItems: lineItems,
            intervalSetResult: intervalSetResult,
            attributionTiers: loadedDataSet.attributionTiers,
            compositeAllLabel: loadedDataSet.compositeAllLabel,
            intervalStatsHolderMap: intervalStatsHolderMap,
            sequences: sequences
          };

          return dataSet;
        }
      };


      // createStatColumns is helper to create the set of columns requested in statSet options with all intervals on one row.
      let createStatColumnsForInterval = function (statSet, intervalInfo, intervalMode, intervalStats, reportControl: Solovis.Reports.ReportControl) {
        let columns = createStatColumns(statSet, intervalMode, intervalStats, reportControl);

        let intervalCode = intervalInfo.intervalRequest.code;

        let overallIdPrefix = "interval." + intervalCode + ".";
        let overallFieldPrefix = "byIntervalCode." + intervalCode + ".";

        let maxSuperHeaderCount = 0;
        $(columns).each(function (i, col: any) {
          if (col.superHeaders) {
            maxSuperHeaderCount = Math.max(maxSuperHeaderCount, col.superHeaders.length);
          }
        });

        $(columns).each(function (i, col: any) {
          if (col.id === "intervalLabel") {
            // Interval label will be visible in the super header.
            col.hiddenByDefault = true;
          }

          col.id = overallIdPrefix + col.id;

          if (typeof col.field === "string") {
            col.field = overallFieldPrefix + col.field;
          }
          else if (typeof col.field === "function") {
            // The function is responsible for checking column interval via col.custom.intervalCode, so leave untouched.
          }
          else {
            col.field = function () {
              throw "Column should have been string or function: " + col.id;
            };
          }

          let superHeaders = col.superHeaders ? col.superHeaders.slice(0) : [];

          // Ensure interval label will be at same depth.
          while (superHeaders.length < maxSuperHeaderCount) {
            superHeaders.push("");
          }
          superHeaders.push(intervalInfo.label);

          col.superHeaders = superHeaders;

          if (!col.custom) {
            col.custom = {};
          }
          col.custom.intervalCode = intervalCode;
        });

        return columns;
      };


      // createStatColumns is helper to create the set of columns requested in statSet options.
      let createStatColumns = function (statSet, intervalMode, intervalStats, reportControl: Solovis.Reports.ReportControl) {
        let columns = [];

        let reportParameters = reportControl.getParameterValues();
        let otherOptions = reportParameters.otherOptions;
        if (!otherOptions) {
          otherOptions = {};
        }

        let tryGetPerformanceVariationForCode = function (variationCode) {
          let customEnvironmentUserData = reportControl.portalServices().customEnvironmentUserData();

          if (customEnvironmentUserData && customEnvironmentUserData.ModelSettings) {
            let modelSettings = customEnvironmentUserData.ModelSettings;
            if (modelSettings && modelSettings.performanceVariationsConfig && !_.isEmpty(modelSettings.performanceVariationsConfig.variations)) {
              for (var co = 0; co < modelSettings.performanceVariationsConfig.variations.length; co++) {
                let variation = modelSettings.performanceVariationsConfig.variations[co];
                if (variation.code === variationCode) {
                  return variation;
                }
              }
            }
          }

          return null;
        };

        if (intervalMode === "IntervalSet") {
          columns.push({
            id: "intervalLabel", field: "intervalLabel", label: "Interval", dataType: "string"
          });
          columns.push({
            id: "intervalCode", field: "intervalCode", label: "Interval Code", dataType: "string",
            hiddenByDefault: true,
            superHeaders: ["Interval Request"]
          });
          columns.push({
            id: "intervalTimeRangeDescription", field: "intervalTimeRangeDescription", label: "Description", dataType: "string",
            hiddenByDefault: true,
            superHeaders: ["Interval Request"]
          });
        }

        columns.push({
          id: "intervalFromDayId", field: "intervalFromDayId", label: "From", dataType: "dayId",
          hiddenByDefault: true,
          superHeaders: ["Interval Request"]
        });
        columns.push({
          id: "intervalToDayId", field: "intervalToDayId", label: "To", dataType: "dayId",
          hiddenByDefault: true,
          superHeaders: ["Interval Request"]
        });

        columns.push({
          id: "twrFromDayIdUsed", field: "twrFromDayIdUsed", label: "TWR From Date Used", dataType: "dayId", hiddenByDefault: true
        });
        columns.push({
          id: "twrToDayIdUsed", field: "twrToDayIdUsed", label: "TWR To Date Used", dataType: "dayId", hiddenByDefault: true
        });

        columns.push({
          id: "txBasedFromDayIdUsed", field: "txBasedFromDayIdUsed", label: "TX-Based From Date Used", dataType: "dayId", hiddenByDefault: true
        });
        columns.push({
          id: "txBasedToDayIdUsed", field: "txBasedToDayIdUsed", label: "TX-Based To Date Used", dataType: "dayId", hiddenByDefault: true
        });


        if (statSet.values) {
          if (statSet.values.endValue) {
            columns.push({
              id: "values.endValue.totalValue",
              field: "valuesResult.endValue.totalValue",
              label: "Total Value",
              dataType: "currency",
              superHeaders: ["End Value"]
            });
            if (statSet.values.units) {
              columns.push({
                id: "values.endValue.units",
                field: "valuesResult.endValue.units",
                label: "Units",
                dataType: "currency",
                superHeaders: ["End Value"]
              });
            }
            if (statSet.values.unitPrice) {
              columns.push({
                id: "values.endValue.unitPrice",
                field: "valuesResult.endValue.unitPrice",
                label: "Unit Price",
                dataType: "currency",
                superHeaders: ["End Value"]
              });
            }
            columns.push({
              id: "values.endValue.valueType",
              field: "valuesResult.endValue.valueType",
              label: "Value Type",
              dataType: "valueTypeId",
              superHeaders: ["End Value"]
            });
            if (statSet.values.ownershipPercentage) {
              columns.push({
                id: "values.endOwnershipPercentage",
                field: "valuesResult.endOwnershipPercentage",
                label: "Ownership %",
                dataType: "percentage",
                superHeaders: ["End Value"]
              });
            }
          }

          if (statSet.values.accruedIncomeEndValue) {
            columns.push({
              id: "values.accruedIncomeEndValue.value",
              field: "valuesResult.accruedIncomeEndValue.value",
              label: "Accrued Income",
              dataType: "currency",
              superHeaders: ["End Value"]
            });
          }

          if (statSet.values.startValue) {
            columns.push({
              id: "values.startValue.totalValue",
              field: "valuesResult.startValue.totalValue",
              label: "Total Value",
              dataType: "currency",
              superHeaders: ["Start Value"]
            });
            if (statSet.values.units) {
              columns.push({
                id: "values.startValue.units",
                field: "valuesResult.startValue.units",
                label: "Units",
                dataType: "currency",
                superHeaders: ["Start Value"]
              });
            }
            if (statSet.values.unitPrice) {
              columns.push({
                id: "values.startValue.unitPrice",
                field: "valuesResult.startValue.unitPrice",
                label: "Unit Price",
                dataType: "currency",
                superHeaders: ["Start Value"]
              });
            }
            columns.push({
              id: "values.startValue.valueType",
              field: "valuesResult.startValue.valueType",
              label: "Value Type",
              dataType: "valueTypeId",
              superHeaders: ["Start Value"]
            });
            if (statSet.values.ownershipPercentage) {
              columns.push({
                id: "values.startOwnershipPercentage",
                field: "valuesResult.startOwnershipPercentage",
                label: "Ownership %",
                dataType: "percentage",
                superHeaders: ["Start Value"]
              });
            }
          }

          if (statSet.values.accruedIncomeStartValue) {
            columns.push({
              id: "values.accruedIncomeStartValue.value",
              field: "valuesResult.accruedIncomeStartValue.value",
              label: "Accrued Income",
              dataType: "currency",
              superHeaders: ["Start Value"]
            });
          }

          if (statSet.values.lastValueAboveValueTypeThresholdId) {
            columns.push({
              id: "values.lastValueAboveValueTypeThreshold.totalValue",
              field: "valuesResult.lastValueAboveValueTypeThreshold.totalValue",
              label: "Total Value",
              dataType: "currency",
              superHeaders: ["Last Value Above Value Type Threshold"]
            });
            columns.push({
              id: "values.lastValueAboveValueTypeThreshold.valueType",
              field: "valuesResult.lastValueAboveValueTypeThreshold.valueType",
              label: "Value Type",
              dataType: "valueTypeId",
              superHeaders: ["Last Value Above Value Type Threshold"]
            });
            columns.push({
              id: "values.lastValueAboveValueTypeThreshold.dayId",
              field: "valuesResult.lastValueAboveValueTypeThreshold.dayId",
              label: "Date",
              dataType: "dayId",
              superHeaders: ["Last Value Above Value Type Threshold"]
            });
          }

          if (statSet.values.netValueGainLoss) {
            columns.push({
              id: "values.netValueGainLoss",
              field: "valuesResult.netValueGainLoss",
              label: "Net Value Gain/Loss",
              dataType: "currency"
            });
          }

          if (statSet.values.income) {
            columns.push({
              id: "values.income",
              field: "valuesResult.income",
              label: "Income",
              dataType: "currency"
            });
          }

          if (statSet.values.averageBalance) {
            columns.push({
              id: "values.averageBalance",
              field: "valuesResult.averageBalance",
              label: "Average Balance",
              dataType: "currency"
            });
          }

        }

        if (statSet.values && statSet.values.localValues) {
          if (statSet.values.endValue) {
            columns.push({
              id: "values.endValueLocal.totalValue",
              field: "valuesResult.endValueLocal.totalValue",
              label: "Total Value",
              dataType: "currency",
              superHeaders: ["End Value (Local)"]
            });
            if (statSet.values.units) {
              columns.push({
                id: "values.endValueLocal.units",
                field: "valuesResult.endValueLocal.units",
                label: "Units",
                dataType: "currency",
                superHeaders: ["End Value (Local)"]
              });
            }
            if (statSet.values.unitPrice) {
              columns.push({
                id: "values.endValueLocal.unitPrice",
                field: "valuesResult.endValueLocal.unitPrice",
                label: "Unit Price",
                dataType: "currency",
                superHeaders: ["End Value (Local)"]
              });
            }
            columns.push({
              id: "values.endValueLocal.valueType",
              field: "valuesResult.endValueLocal.valueType",
              label: "Value Type",
              dataType: "valueTypeId",
              superHeaders: ["End Value (Local)"]
            });
          }
          if (statSet.values.startValue) {
            columns.push({
              id: "values.startValueLocal.totalValue",
              field: "valuesResult.startValueLocal.totalValue",
              label: "Total Value",
              dataType: "currency",
              superHeaders: ["Start Value (Local)"]
            });
            if (statSet.values.units) {
              columns.push({
                id: "values.startValueLocal.units",
                field: "valuesResult.startValueLocal.units",
                label: "Units",
                dataType: "currency",
                superHeaders: ["Start Value (Local)"]
              });
            }
            if (statSet.values.unitPrice) {
              columns.push({
                id: "values.startValueLocal.unitPrice",
                field: "valuesResult.startValueLocal.unitPrice",
                label: "Unit Price",
                dataType: "currency",
                superHeaders: ["Start Value (Local)"]
              });
            }
            columns.push({
              id: "values.startValueLocal.valueType",
              field: "valuesResult.startValueLocal.valueType",
              label: "Value Type",
              dataType: "valueTypeId",
              superHeaders: ["Start Value (Local)"]
            });
          }

          if (statSet.values.lastValueAboveValueTypeThresholdId) {
            columns.push({
              id: "values.lastValueAboveValueTypeThresholdLocal.totalValue",
              field: "valuesResult.lastValueAboveValueTypeThresholdLocal.totalValue",
              label: "Total Value",
              dataType: "currency",
              superHeaders: ["Last Value Above Value Type Threshold (Local)"]
            });
            columns.push({
              id: "values.lastValueAboveValueTypeThresholdLocal.valueType",
              field: "valuesResult.lastValueAboveValueTypeThresholdLocal.valueType",
              label: "Value Type",
              dataType: "valueTypeId",
              superHeaders: ["Last Value Above Value Type Threshold (Local)"]
            });
            columns.push({
              id: "values.lastValueAboveValueTypeThresholdLocal.dayId",
              field: "valuesResult.lastValueAboveValueTypeThresholdLocal.dayId",
              label: "Date",
              dataType: "dayId",
              superHeaders: ["Last Value Above Value Type Threshold (Local)"]
            });
          }

          if (statSet.values.netValueGainLoss) {
            columns.push({
              id: "values.netValueGainLossLocal",
              field: "valuesResult.netValueGainLossLocal",
              label: "Net Value Gain/Loss (Local)",
              dataType: "currency"
            });
          }

          if (statSet.values.income) {
            columns.push({
              id: "values.incomeLocal",
              field: "valuesResult.incomeLocal",
              label: "Income (Local)",
              dataType: "currency"
            });
          }

          if (statSet.values.averageBalance) {
            columns.push({
              id: "values.averageBalanceLocal",
              field: "valuesResult.averageBalanceLocal",
              label: "Average Balance (Local)",
              dataType: "currency"
            });
          }
        }

        if (statSet.values && !_.isEmpty(statSet.values.twrVariations)) {
          statSet.values.twrVariations.forEach((variationCode) => {
            var variationEntry = tryGetPerformanceVariationForCode(variationCode);
            if (variationEntry) {
              if (statSet.values.income) {
                columns.push({
                  id: "values." + variationCode + ".income",
                  field: "valuesResult.twrVariationByCode." + variationCode + ".income",
                  label: "Income",
                  dataType: "currency",
                  superHeaders: [variationEntry.label],
                });
              }
              if (statSet.values.netValueGainLoss) {
                columns.push({
                  id: "values." + variationCode + ".netValueGainLoss",
                  field: "valuesResult.twrVariationByCode." + variationCode + ".netValueGainLoss",
                  label: "Net Value Gain/Loss",
                  dataType: "currency",
                  superHeaders: [variationEntry.label],
                });
              }

              if (statSet.values.averageBalance) {
                columns.push({
                  id: "values." + variationCode + ".averageBalance",
                  field: "valuesResult.twrVariationByCode." + variationCode + ".averageBalance",
                  label: "Average Balance",
                  dataType: "currency",
                  superHeaders: [variationEntry.label],
                });
              }

              if (statSet.values.localValues) {
                if (statSet.values.income) {
                  columns.push({
                    id: "values." + variationCode + ".incomeLocal",
                    field: "valuesResult.twrVariationByCode." + variationCode + ".incomeLocal",
                    label: "Income (Local)",
                    dataType: "currency",
                    superHeaders: [variationEntry.label],
                  });
                }
                if (statSet.values.netValueGainLoss) {
                  columns.push({
                    id: "values." + variationCode + ".netValueGainLossLocal",
                    field: "valuesResult.twrVariationByCode." + variationCode + ".netValueGainLossLocal",
                    label: "Net Value Gain/Loss (Local)",
                    dataType: "currency",
                    superHeaders: [variationEntry.label],
                  });
                }
                if (statSet.values.averageBalance) {
                  columns.push({
                    id: "values." + variationCode + ".averageBalanceLocal",
                    field: "valuesResult.twrVariationByCode." + variationCode + ".averageBalanceLocal",
                    label: "Average Balance (Local)",
                    dataType: "currency",
                    superHeaders: [variationEntry.label],
                  });
                }
              }
            }
          });
        }

        // Basic Stats
        let addStatsColumn = function (statsField, forBenchmark, sequence?, sequenceIndex?) {
          let label = (sequence ? sequenceStatsFieldLabels : statsFieldLabels)[statsField];
          if (!label) {
            label = statsField;
          }

          let dataType = statsFieldDataTypes[statsField];
          if (!dataType) {
            dataType = statsField;
          }

          let colHelp = statsFieldHelp[statsField];

          // id
          let id = forBenchmark ? "benchmark." + statsField : statsField;
          if (sequence) {
            id = "seq." + (sequence.code ? sequence.code : ("" + sequenceIndex)) + id;
          }

          // field
          let field = forBenchmark ? ("basicStats.benchmarkStats." + statsField) : ("basicStats." + statsField);
          if (sequence) {
            field = "bySequence." + sequenceIndex + "." + field;
          }

          // superHeaders
          let superHeaders = null;
          if (forBenchmark) {
            superHeaders = ["Benchmark"];
          }

          // custom
          let custom: any = {
            statsField: statsField,
            forBenchmark: forBenchmark
          };
          if (sequence) {
            custom.sequence = sequence;
          }

          let col = {
            id: id,
            field: field,
            label: label,
            dataType: dataType,
            superHeaders: superHeaders,
            help: colHelp,
            custom: custom
          };

          columns.push(col);

          return col;
        };

        let sequenceStatsFieldList = [
          "cumulativeReturn",
          "annualizedReturn",
          "standardDeviation",
          "annualizedStandardDeviation",
          "variance",
          "downsideStandardDeviation",
          "mean",
          "sampleSkewness",
          "sampleExcessKurtosis"
        ];

        let coreStatsFieldList = sequenceStatsFieldList.concat([
          "cumulativeIrr",
          "annualizedIrr",
          "endOfDayCumulativeIrr",
          "endOfDayAnnualizedIrr",
        ]);

        let benchmarkedStatsFieldList = [
          "correlation",
          "covariance",
          "deltaMean",
          "deltaStandardDeviation",
          "downsideDeltaStandardDeviation"
        ];

        let addGainLossColumnsIfNeeded = function (statSet, sequence?, sequenceIndex?) {
          if ((sequence && sequence.gainLossSummary) || (!sequence && statSet.gainLossSummary)) {
            let superHeaders;
            if (!sequence) {
              superHeaders = ["Asset Monthly Gain/Loss Summary"];
            }
            else {
              superHeaders = ["Asset Gain/Loss Summary"];
            }
            let gainLossHelp = { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_gain_loss_summary" };

            let gainLossIdPrefix = "gainLossSummary.";
            if (sequence) {
              gainLossIdPrefix = "seq." + (sequence.code ? sequence.code : ("" + sequenceIndex)) + gainLossIdPrefix;
            }

            let gainLossFieldPrefix = "gainLossSummary.";
            if (sequence) {
              gainLossFieldPrefix = "bySequence." + sequenceIndex + "." + gainLossFieldPrefix;
            }

            columns.push({ id: gainLossIdPrefix + "averageGain", field: gainLossFieldPrefix + "averageGain", label: "Average Gain", dataType: "percentage", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "averageLoss", field: gainLossFieldPrefix + "averageLoss", label: "Average Loss", dataType: "percentage", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "bestPeriodReturn", field: gainLossFieldPrefix + "bestPeriodReturn", label: "Best Period Return", dataType: "percentage", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "worstPeriodReturn", field: gainLossFieldPrefix + "worstPeriodReturn", label: "Worst Period Return", dataType: "percentage", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "positivePeriods", field: gainLossFieldPrefix + "positivePeriods", label: "Positive Periods", dataType: "integer", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "negativePeriods", field: gainLossFieldPrefix + "negativePeriods", label: "Negative Periods", dataType: "integer", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "periodCount", field: gainLossFieldPrefix + "periodCount", label: "Period Count", dataType: "integer", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "gainLossRatio", field: gainLossFieldPrefix + "gainLossRatio", label: "Gain/Loss Ratio", dataType: "percentage", superHeaders: superHeaders.slice(0), help: gainLossHelp });
            columns.push({ id: gainLossIdPrefix + "periodsOverBenchmark", field: gainLossFieldPrefix + "periodsOverBenchmark", label: "Periods Over Benchmark", dataType: "integer", superHeaders: superHeaders.slice(0), help: gainLossHelp });
          }
        };

        let addSequenceColumns = function (statSet, sequence: Solovis.Reports.Parameters.Stats.SequenceOptions, sequenceIndex) {
          let seqPrefix = "seq." + sequence.code;

          let firstSeqColIndex = columns.length;

          $(sequenceStatsFieldList).each(function (i, statsField: any) {
            if (sequence.basicStats && sequence.basicStats[statsField]) {
              addStatsColumn(statsField, false, sequence, sequenceIndex);
            }
          });

          $(benchmarkedStatsFieldList).each(function (i, statsField: any) {
            if (sequence.benchmarkedBasicStats && sequence.benchmarkedBasicStats[statsField]) {
              addStatsColumn(statsField, false, sequence, sequenceIndex);
            }
          });

          if (statSet.basicStatsBenchmarkRef && !statSet.basicStatsBenchmarkRef.useNone) {
            // Only include if the basic stats benchmark ref was included.
            $(sequenceStatsFieldList).each(function (i, statsField: any) {
              if (sequence.basicStats && sequence.basicStats[statsField]) {
                addStatsColumn(statsField, true, sequence, sequenceIndex);
              }
            });
          }

          addGainLossColumnsIfNeeded(statSet, sequence, sequenceIndex);

          let seqIdPrefix = "seq." + (sequence.code ? sequence.code : ("" + sequenceIndex)) + ".";
          let seqFieldPrefix = "bySequence." + sequenceIndex + ".";

          // Beta/Alpha
          if (sequence.betaAlpha) {
            columns.push({ id: seqIdPrefix + "betaAlpha.beta", field: seqFieldPrefix + "betaAlphaResult.beta", label: "Beta", dataType: "number", formatHint: { scale: 2 } });
            columns.push({ id: seqIdPrefix + "betaAlpha.alpha", field: seqFieldPrefix + "betaAlphaResult.alpha", label: "Alpha", dataType: "percentage", formatHint: { scale: 2 } });
            columns.push({ id: seqIdPrefix + "betaAlpha.rSquared", field: seqFieldPrefix + "betaAlphaResult.rSquared", label: "Beta/Alpha R-Squared", dataType: "number", formatHint: { scale: 2 } });

            if (sequence.betaAlpha.annualizedAlpha) {
              columns.push({ id: seqIdPrefix + "betaAlpha.annualizedAlpha", field: seqFieldPrefix + "betaAlphaResult.annualizedAlpha", label: "Annualized Alpha", dataType: "percentage", formatHint: { scale: 2 } });
            }

            if (sequence.betaAlpha.annualizedTreynorRatio) {
              columns.push({ id: seqIdPrefix + "betaAlpha.annualizedTreynorRatio", field: seqFieldPrefix + "betaAlphaResult.annualizedTreynorRatio", label: "Annualized Treynor Ratio", dataType: "number", formatHint: { scale: 2 } });
            }
          }

          // Information Ratio
          if (sequence.informationRatio) {
            columns.push({ id: seqIdPrefix + "informationRatio", field: seqFieldPrefix + "informationRatioResult.informationRatio", label: "Information Ratio", dataType: "number", formatHint: { scale: 2 } });
          }

          let seqColEndIndex = columns.length;

          ///
          /// We will put one overarching superheader over all of the sequence columns.
          ///

          // Determine max superHeaders length.
          let maxSuperHeaderCount = 0;
          for (let i = firstSeqColIndex; i < seqColEndIndex; i++) {
            let col = columns[i];
            let superHeaderCount = col.superHeaders ? col.superHeaders.length : 0;
            maxSuperHeaderCount = Math.max(maxSuperHeaderCount, superHeaderCount);
          }

          // Add sequence superHeader to each of those columns (has to be at same level to avoid having that header appear uneven).
          let seqSuperHeader = sequence.label ? sequence.label : "(Unlabeled Sequence)";
          for (let i = firstSeqColIndex; i < seqColEndIndex; i++) {
            let col = columns[i];
            if (!col.superHeaders) {
              col.superHeaders = [];
            }

            let superHeaderCount = col.superHeaders.length;

            // Pad out super headers to max seen on this sequence to align the sequence superheader.
            while (superHeaderCount < maxSuperHeaderCount) {
              col.superHeaders.push("");
              superHeaderCount++;
            }

            col.superHeaders.push(seqSuperHeader);
          }
        };

        // Determine showHybridAnnCumColumns and showTwrActiveReturnColumns are true/false based on parameters and site default settings.
        let siteReportSettings = reportControl.getSiteReportSettings();
        let siteCustomReportSettings = siteReportSettings ? siteReportSettings.custom : null;

        let showHybridAnnCumColumns = false;
        if (siteCustomReportSettings && Solovis.isNotNull(siteCustomReportSettings.showHybridAnnCumColumns)) {
          showHybridAnnCumColumns = !!siteCustomReportSettings.showHybridAnnCumColumns;
        }
        if (otherOptions.showHybridAnnCumColumns === "Yes") {
          showHybridAnnCumColumns = true;
        }
        else if (otherOptions.showHybridAnnCumColumns === "No") {
          showHybridAnnCumColumns = false;
        }

        let showTwrActiveReturnColumns = false;
        if (siteCustomReportSettings && Solovis.isNotNull(siteCustomReportSettings.showTwrActiveReturnColumns)) {
          showTwrActiveReturnColumns = !!siteCustomReportSettings.showTwrActiveReturnColumns;
        }
        if (otherOptions.showTwrActiveReturnColumns === "Yes") {
          showTwrActiveReturnColumns = true;
        }
        else if (otherOptions.showTwrActiveReturnColumns === "No") {
          showTwrActiveReturnColumns = false;
        }

        let willShowHybridAnnCumColumnsForTwr = false;
        let willShowHybridAnnCumColumnsForIrr = false;
        if (showHybridAnnCumColumns) {
          if (statSet.basicStats) {
            if (statSet.basicStats.annualizedReturn && statSet.basicStats.cumulativeReturn) {
              willShowHybridAnnCumColumnsForTwr = true;
            }
            if (statSet.basicStats.annualizedIrr && statSet.basicStats.cumulativeIrr) {
              willShowHybridAnnCumColumnsForIrr = true;
            }
          }
        }

        let createRowIntervalValueGetter = function (valueField) {
          return function (row, col) {
            let fieldRef = valueField;
            if (col.custom && col.custom.intervalCode) {
              fieldRef = "byIntervalCode." + col.custom.intervalCode + "." + fieldRef;
            }

            return Solovis.resolveObject(fieldRef, row);
          };
        };

        // Create getter that returns information about interval range in use for a given row and column.
        let intervalFromDayIdGetter = createRowIntervalValueGetter("intervalFromDayId");
        let intervalToDayIdGetter = createRowIntervalValueGetter("intervalToDayId");

        let twrFromDayIdUsedGetter = createRowIntervalValueGetter("twrFromDayIdUsed");
        let twrToDayIdUsedGetter = createRowIntervalValueGetter("twrToDayIdUsed");

        let txBasedFromDayIdUsedGetter = createRowIntervalValueGetter("txBasedFromDayIdUsed");
        let txBasedToDayIdUsedGetter = createRowIntervalValueGetter("txBasedToDayIdUsed");

        let intervalCodeGetter = createRowIntervalValueGetter("intervalCode");

        let genericIntervalRangeGetter = function (row, col, dataFromGetter, dataToGetter) {
          let intervalCode = intervalCodeGetter(row, col);
          let intervalFromDayId = intervalFromDayIdGetter(row, col);
          let intervalToDayId = intervalToDayIdGetter(row, col);

          let intervalRange: any = {};
          if (Solovis.isNotNull(intervalFromDayId)) {
            if (Solovis.isNotNull(intervalToDayId)) {
              intervalRange.intervalDays = intervalToDayId - intervalFromDayId;
            }
          }
          else {
            intervalRange.isSinceInception = true;
          }

          let dataFromDayIdUsed = dataFromGetter(row, col);
          let dataToDayIdUsed = dataToGetter(row, col);

          if (Solovis.isNotNull(dataFromDayIdUsed) && Solovis.isNotNull(dataToDayIdUsed)) {
            intervalRange.hasDataOnIntervalStart = dataFromDayIdUsed === intervalFromDayId;
            intervalRange.dataDays = dataToDayIdUsed - dataFromDayIdUsed;
          }

          if (intervalCode && intervalCode.indexOf("Since") === 0) {
            intervalRange.isSinceStyleInterval = true;
          }

          return intervalRange;
        };

        let cellIrrIntervalRangeGetter = function (row, col) {
          return genericIntervalRangeGetter(row, col, txBasedFromDayIdUsedGetter, txBasedToDayIdUsedGetter);
        };

        let cellTwrIntervalRangeGetter = function (row, col) {
          return genericIntervalRangeGetter(row, col, twrFromDayIdUsedGetter, twrToDayIdUsedGetter);
        };


        $(coreStatsFieldList).each(function (i, statsField: any) {
          if (statSet.basicStats && statSet.basicStats[statsField]) {
            var addedCol: any = addStatsColumn(statsField, false);

            if (statsField === "annualizedReturn" || statsField === "cumulativeReturn") {
              if (willShowHybridAnnCumColumnsForTwr) {
                addedCol.hiddenByDefault = true;
              }
            }
            if (statsField === "annualizedIrr" || statsField === "cumulativeIrr") {
              if (willShowHybridAnnCumColumnsForIrr) {
                addedCol.hiddenByDefault = true;
              }
            }
          }

          if (statsField === "annualizedReturn") {
            if (willShowHybridAnnCumColumnsForTwr) {
              columns.push(
                Solovis.Reports.ReportHelpers.StatsColumnHelper.createAnnualizedCumulativeColumnTemplate(
                  createRowIntervalValueGetter("basicStats.annualizedReturn"),
                  createRowIntervalValueGetter("basicStats.cumulativeReturn"),
                  {
                    id: "annCumTwrReturn",
                    label: "Ann./Cum. Return",
                    custom: {
                      statsField: "annCumTwrReturn"
                    }
                  },
                  cellTwrIntervalRangeGetter)
              );
            }

            // This is where we will inject TWR variations if needed.
            if (statSet.basicStats && !_.isEmpty(statSet.basicStats.twrVariations)) {
              for (var variationCo = 0; variationCo < statSet.basicStats.twrVariations.length; variationCo++) {
                var variationCode = statSet.basicStats.twrVariations[variationCo];
                var variationEntry = tryGetPerformanceVariationForCode(variationCode);
                if (variationEntry) {
                  if (statSet.basicStats.cumulativeReturn) {
                    var twrVariationCumulativeReturnCol = {
                      id: "twrVariation." + variationCode + ".cumulativeReturn",
                      field: "twrVariationByCode." + variationCode + ".cumulativeReturn",
                      label: "Cumulative Return",
                      dataType: "percentage",
                      superHeaders: [variationEntry.label],

                      custom: {
                        performanceVariationCode: variationCode,
                        statsField: "cumulativeReturn"
                      }
                    };

                    columns.push(twrVariationCumulativeReturnCol);
                  }

                  if (statSet.basicStats.annualizedReturn) {
                    var twrVariationAnnualizedTwrCol = {
                      id: "twrVariation." + variationCode + ".annualizedReturn",
                      field: "twrVariationByCode." + variationCode + ".annualizedReturn",
                      label: "Annualized Return",
                      dataType: "percentage",
                      superHeaders: [variationEntry.label],

                      custom: {
                        performanceVariationCode: variationCode,
                        statsField: "annualizedReturn"
                      }
                    };

                    columns.push(twrVariationAnnualizedTwrCol);
                  }

                  if (willShowHybridAnnCumColumnsForTwr) {
                    columns.push(
                      Solovis.Reports.ReportHelpers.StatsColumnHelper.createAnnualizedCumulativeColumnTemplate(
                        createRowIntervalValueGetter("twrVariationByCode." + variationCode + ".annualizedReturn"),
                        createRowIntervalValueGetter("twrVariationByCode." + variationCode + ".cumulativeReturn"),
                        {
                          id: "annCumTwrVariation." + variationCode + ".annCumReturn",
                          label: "Ann./Cum. Return",
                          superHeaders: [variationEntry.label],

                          custom: {
                            statsField: "annCumTwrReturn"
                          }
                        },
                        cellTwrIntervalRangeGetter)
                    );
                  }
                }
              }
            }
          }
        });

        // The hybrid ann./cum. IRR column.
        if (willShowHybridAnnCumColumnsForIrr) {
          columns.push(
            Solovis.Reports.ReportHelpers.StatsColumnHelper.createAnnualizedCumulativeColumnTemplate(
              createRowIntervalValueGetter("basicStats.annualizedIrr"),
              createRowIntervalValueGetter("basicStats.cumulativeIrr"),
              {
                id: "annCumIrr",
                label: "Ann./Cum. IRR",
                custom: {
                  statsField: "annCumIrr"

                }
              },
              cellIrrIntervalRangeGetter)
          );
        }

        if (statSet.basicStats && !_.isEmpty(statSet.basicStats.irrVariations)) {
          // IRR variations.
          for (var variationCo = 0; variationCo < statSet.basicStats.irrVariations.length; variationCo++) {
            var variationCode = statSet.basicStats.irrVariations[variationCo];
            var variationEntry = tryGetPerformanceVariationForCode(variationCode);
            if (variationEntry) {
              if (statSet.basicStats.cumulativeIrr) {
                var irrVariationCumulativeIrrCol = {
                  id: "irrVariation." + variationCode + ".cumulativeIrr",
                  field: "irrVariationByCode." + variationCode + ".cumulativeIrr",
                  label: "Cumulative IRR",
                  dataType: "percentage",
                  superHeaders: [variationEntry.label],

                  custom: {
                    performanceVariationCode: variationCode,
                    statsField: "cumulativeIrr"
                  }
                };

                columns.push(irrVariationCumulativeIrrCol);
              }

              if (statSet.basicStats.annualizedIrr) {
                var irrVariationAnnualizedIrrCol = {
                  id: "irrVariation." + variationCode + ".annualizedIrr",
                  field: "irrVariationByCode." + variationCode + ".annualizedIrr",
                  label: "Annualized IRR",
                  dataType: "percentage",
                  superHeaders: [variationEntry.label],

                  custom: {
                    performanceVariationCode: variationCode,
                    statsField: "annualizedIrr"
                  }
                };

                columns.push(irrVariationAnnualizedIrrCol);
              }

              if (statSet.basicStats.annualizedIrr || statSet.basicStats.cumulativeIrr) {
                columns.push({
                  id: "irrVariation." + variationCode + ".txBasedFromDayIdUsed",
                  field: "irrVariationByCode." + variationCode + ".txBasedFromDayIdUsed",
                  label: "TX-Based From Date Used",
                  dataType: "dayId",
                  hiddenByDefault: true,
                  superHeaders: [variationEntry.label],
                });
                columns.push({
                  id: "irrVariation." + variationCode + ".txBasedToDayIdUsed",
                  field: "irrVariationByCode." + variationCode + ".txBasedToDayIdUsed",
                  label: "TX-Based To Date Used",
                  dataType: "dayId",
                  hiddenByDefault: true,
                  superHeaders: [variationEntry.label],
                });
              }

              if (willShowHybridAnnCumColumnsForIrr) {
                let irrPerfFromIdGetter = createRowIntervalValueGetter("irrVariationByCode." + variationCode + ".txBasedFromDayIdUsed");
                let irrPerfToIdGetter = createRowIntervalValueGetter("irrVariationByCode." + variationCode + ".txBasedToDayIdUsed");
                columns.push(
                  Solovis.Reports.ReportHelpers.StatsColumnHelper.createAnnualizedCumulativeColumnTemplate(
                    createRowIntervalValueGetter("irrVariationByCode." + variationCode + ".annualizedIrr"),
                    createRowIntervalValueGetter("irrVariationByCode." + variationCode + ".cumulativeIrr"),
                    {
                      id: "annCumIrrVariation." + variationCode + ".annCumIrr",
                      label: "Ann./Cum. IRR",
                      superHeaders: [variationEntry.label],

                      custom: {
                        statsField: "annCumIrr"
                      }
                    },
                    (row, col) => genericIntervalRangeGetter(row, col, irrPerfFromIdGetter, irrPerfToIdGetter))
                );
              }

            }
          }
        }

        $(benchmarkedStatsFieldList).each(function (i, statsField: any) {
          if (statSet.benchmarkedBasicStats && statSet.benchmarkedBasicStats[statsField]) {
            addStatsColumn(statsField, false);
          }
        });

        // Benchmark Basic Stats
        if (statSet.basicStatsBenchmarkRef && !statSet.basicStatsBenchmarkRef.useNone) {
          // Only include if the basic stats benchmark ref was included.
          $(coreStatsFieldList).each(function (i, statsField: any) {
            if (statsField !== "annualizedIrr" && statsField !== "cumulativeIrr" && statsField !== "endOfDayAnnualizedIrr" && statsField !== "endOfDayCumulativeIrr") {
              if (statSet.basicStats && statSet.basicStats[statsField]) {
                var addedCol: any = addStatsColumn(statsField, true);

                if (statsField === "annualizedReturn" || statsField === "cumulativeReturn") {
                  if (willShowHybridAnnCumColumnsForTwr) {
                    addedCol.hiddenByDefault = true;
                  }
                }

                if (statsField === "cumulativeReturn") {
                  if (showTwrActiveReturnColumns) {
                    columns.push(
                      Solovis.Reports.ReportHelpers.StatsColumnHelper.createActiveReturnColumnTemplate(
                        createRowIntervalValueGetter("basicStats.cumulativeReturn"),
                        createRowIntervalValueGetter("basicStats.benchmarkStats.cumulativeReturn"),
                        {
                          id: "activeCumulativeReturn",
                          label: "Active Cumulative Return",
                          superHeaders: ["Benchmark"]
                        })
                    );
                  }
                }

                if (statsField === "annualizedReturn") {
                  if (showTwrActiveReturnColumns) {
                    columns.push(
                      Solovis.Reports.ReportHelpers.StatsColumnHelper.createActiveReturnColumnTemplate(
                        createRowIntervalValueGetter("basicStats.annualizedReturn"),
                        createRowIntervalValueGetter("basicStats.benchmarkStats.annualizedReturn"),
                        {
                          id: "activeAnnualizedReturn",
                          label: "Active Annualized Return",
                          superHeaders: ["Benchmark"]
                        })
                    );
                  }

                  if (willShowHybridAnnCumColumnsForTwr) {
                    columns.push(
                      Solovis.Reports.ReportHelpers.StatsColumnHelper.createAnnualizedCumulativeColumnTemplate(
                        createRowIntervalValueGetter("basicStats.benchmarkStats.annualizedReturn"),
                        createRowIntervalValueGetter("basicStats.benchmarkStats.cumulativeReturn"),
                        {
                          id: "benchmark.annCumTwrReturn",
                          label: "Ann./Cum. Return",
                          superHeaders: ["Benchmark"]
                        },
                        cellTwrIntervalRangeGetter)
                    );
                  }
                }
              }
            }
          });
        }

        // Gain/Loss Summary
        addGainLossColumnsIfNeeded(statSet);

        // Peaks And Troughs
        if (statSet.peaksAndTroughs) {
          if (statSet.peaksAndTroughs.maxDrawdown) {
            columns.push({ id: "maxDrawdown", field: "maxDrawdownResult.drawdown", label: "Max Drawdown", dataType: "percentage" });
            columns.push({ id: "maxDrawdownFromDayId", field: "maxDrawdownResult.fromDayId", label: "Max Drawdown - From", dataType: "dayId" });
            columns.push({ id: "maxDrawdownToDayId", field: "maxDrawdownResult.toDayId", label: "Max Drawdown - To", dataType: "dayId" });
            columns.push({ id: "maxDrawdownPeriodsToRecover", field: "maxDrawdownResult.periodsToRecover", label: "Max Drawdown - Periods To Recover", dataType: "integer" });
          }

          if (statSet.peaksAndTroughs.sincePeakPerformance) {
            columns.push({ id: "sincePeak", field: "sincePeakPerformanceResult.sincePeak", label: "Since Peak", dataType: "percentage" });
            columns.push({ id: "sincePeakLastPeakDayId", field: "sincePeakPerformanceResult.lastPeakDayId", label: "Since Peak - Last Peak", dataType: "dayId" });
          }
        }

        // Beta/Alpha
        if (statSet.betaAlpha) {
          columns.push({ id: "betaAlpha.beta", field: "betaAlphaResult.beta", label: "Beta", dataType: "number", formatHint: { scale: 2 } });
          columns.push({ id: "betaAlpha.alpha", field: "betaAlphaResult.alpha", label: "Monthly Alpha", dataType: "percentage", formatHint: { scale: 2 } });
          columns.push({ id: "betaAlpha.rSquared", field: "betaAlphaResult.rSquared", label: "Beta/Alpha R-Squared", dataType: "number", formatHint: { scale: 2 } });

          if (statSet.betaAlpha.annualizedAlpha) {
            columns.push({ id: "betaAlpha.annualizedAlpha", field: "betaAlphaResult.annualizedAlpha", label: "Annualized Alpha", dataType: "percentage", formatHint: { scale: 2 } });
          }

          if (statSet.betaAlpha.annualizedTreynorRatio) {
            columns.push({ id: "betaAlpha.annualizedTreynorRatio", field: "betaAlphaResult.annualizedTreynorRatio", label: "Annualized Treynor Ratio", dataType: "number", formatHint: { scale: 2 } });
          }
        }

        // Sortino Ratio
        if (statSet.sortinoRatio) {
          columns.push({ id: "sortinoRatio", field: "sortinoRatioResult.monthlySortinoRatio", label: "Monthly Sortino Ratio", dataType: "number", formatHint: { scale: 2 } });
        }

        // Sharpe Ratio
        if (statSet.sharpeRatio) {
          columns.push({ id: "sharpeRatio", field: "sharpeRatioResult.monthlySharpeRatio", label: "Monthly Sharpe Ratio", dataType: "number", formatHint: { scale: 2 } });
          if (statSet.sharpeRatio.annualizedSharpeRatio) {
            columns.push({ id: "annualizedSharpeRatio", field: "sharpeRatioResult.annualizedSharpeRatio", label: "Annualized Sharpe Ratio", dataType: "number", formatHint: { scale: 2 } });
          }
        }

        // Information Ratio
        if (statSet.informationRatio) {
          columns.push({ id: "informationRatio", field: "informationRatioResult.monthlyInformationRatio", label: "Monthly Information Ratio", dataType: "number", formatHint: { scale: 2 } });
        }

        // Upside/Downside Capture Ratio
        if (statSet.upsideDownsideCaptureRatio) {
          columns.push({ id: "upsideCaptureRatio", field: "upsideDownsideCaptureRatioResult.upsideCaptureRatio", label: "Upside Capture Ratio", dataType: "percentage", formatHint: { scale: 2 } });
          columns.push({ id: "downsideCaptureRatio", field: "upsideDownsideCaptureRatioResult.downsideCaptureRatio", label: "Downside Capture Ratio", dataType: "percentage", formatHint: { scale: 2 } });
        }

        // Sequences.                
        if (!_.isEmpty(statSet.sequences)) {
          let sequenceIndex = 0;
          for (let sequence of statSet.sequences) {
            addSequenceColumns(statSet, sequence, sequenceIndex);

            sequenceIndex++;
          }
        }

        // PME
        if (statSet.pme) {
          if (statSet.pme.longNickelsPme) {
            columns.push({ id: "pme.longNickelsPme", field: "pmeResult.longNickelsPme", label: "Long Nickels PME", dataType: "percentage" });

            if (statSet.pme.longNickelsPmeGainLoss) {
              columns.push({
                id: "pme.longNickelsPmeGainLoss.asset", field: "pmeResult.longNickelsPmeGainLoss.asset",
                label: "Asset",
                superHeaders: ["Long Nickels PME Gain/Loss"],
                dataType: "currency"
              });

              columns.push({
                id: "pme.longNickelsPmeGainLoss.benchmark", field: "pmeResult.longNickelsPmeGainLoss.benchmark",
                label: "Benchmark",
                superHeaders: ["Long Nickels PME Gain/Loss"],
                dataType: "currency"
              });

              columns.push({
                id: "pme.longNickelsPmeGainLoss.delta", field: "pmeResult.longNickelsPmeGainLoss.delta",
                label: "Delta",
                superHeaders: ["Long Nickels PME Gain/Loss"],
                dataType: "currency"
              });
            }
          }
          if (statSet.pme.pmePlus) {
            columns.push({ id: "pme.pmePlus", field: "pmeResult.pmePlus", label: "PME+", dataType: "percentage" });
          }
          if (statSet.pme.modifiedPme) {
            columns.push({ id: "pme.modifiedPme", field: "pmeResult.modifiedPme", label: "Modified PME", dataType: "percentage" });
          }
          if (statSet.pme.kaplanSchoarPme) {
            columns.push({ id: "pme.kaplanSchoarPme", field: "pmeResult.kaplanSchoarPme", label: "Kaplan Schoar PME", dataType: "number" });
          }
          if (statSet.pme.directAlpha) {
            columns.push({ id: "pme.directAlpha", field: "pmeResult.directAlpha", label: "Direct Alpha", dataType: "percentage" });
          }
          if (statSet.pme.excessIrr) {
            columns.push({ id: "pme.excessIrr", field: "pmeResult.excessIrr", label: "Excess IRR", dataType: "percentage" });
          }
        }

        // Composite
        if (statSet.composite) {
          if (statSet.composite.contributionToReturn) {
            columns.push({
              id: "composite.contributionToReturn", field: "compositeResult.contributionToReturn", label: "Contribution To Return", dataType: "percentage",
              width: 87
            });
          }
          if (statSet.composite.percentOfAll) {
            columns.push({
              id: "composite.percentOfAll", field: "compositeResult.percentOfAll", label: "% Of All Value", dataType: "percentage",
            });
          }

          // Attribution
          if (statSet.composite.attribution) {
            var attrLabel = "Attribution";
            var totalLabel = "Total";

            var totalAllocationEffectCol = {
              id: "attributionResult.totalAllocationEffect",
              field: "attributionResult.totalAllocationEffect",
              label: "Allocation Effect",
              superHeaders: [totalLabel, attrLabel],
              dataType: "percentage"
            };

            var totalSelectionEffectCol = {
              id: "attributionResult.totalSelectionEffect",
              field: "attributionResult.totalSelectionEffect",
              label: "Selection Effect",
              superHeaders: [totalLabel, attrLabel],
              dataType: "percentage"
            };

            let totalInteractionEffectCol;
            if (statSet.composite.attribution.interactionEffect === "Standalone") {
              totalInteractionEffectCol = {
                id: "attributionResult.totalInteractionEffect",
                field: "attributionResult.totalInteractionEffect",
                label: "Interaction Effect",
                superHeaders: [totalLabel, attrLabel],
                dataType: "percentage"
              };
            }

            var totalLineItemBenchmarkEffectCol;
            if (statSet.composite.attribution.includeLineItemBenchmarkEffect) {
              totalLineItemBenchmarkEffectCol = {
                id: "attributionResult.totalLineItemBenchmarkEffect",
                field: "attributionResult.totalLineItemBenchmarkEffect",
                label: "Line Item Benchmark Effect",
                superHeaders: [totalLabel, attrLabel],
                dataType: "percentage"
              };
            }

            var totalResidualEffectCol = {
              id: "attributionResult.totalResidualEffect",
              field: "attributionResult.totalResidualEffect",
              label: "Residual Effect",
              help: otherHelp.attributionResidualEffect,
              superHeaders: [totalLabel, attrLabel],
              dataType: "percentage",
              hiddenByDefault: true
            };

            var totalActiveReturnCol = {
              id: "attributionResult.totalActiveReturn",
              field: "attributionResult.totalActiveReturn",
              label: "Active Return",
              superHeaders: [totalLabel, attrLabel],
              dataType: "percentage"
            };

            columns.push(totalAllocationEffectCol);
            columns.push(totalSelectionEffectCol);
            if (statSet.composite.attribution.interactionEffect === "Standalone") {
              columns.push(totalInteractionEffectCol);
            }
            if (statSet.composite.attribution.includeLineItemBenchmarkEffect) {
              columns.push(totalLineItemBenchmarkEffectCol);
            }
            columns.push(totalResidualEffectCol);
            columns.push(totalActiveReturnCol);

            $(intervalStats.attributionTiers).each(function (tierIndex, attributionTier: any) {
              var attrIdPrefix = "attribution.tier" + tierIndex;
              var attrFieldPrefix = "attributionByTier.tier" + tierIndex;

              var tierLabel = "Tier " + (tierIndex + 1) + " - " + attributionTier.portfolioLabel + " vs " + attributionTier.benchmarkEntityLabel;

              var attributionGroupReturnCol = {
                id: attrIdPrefix + ".groupReturn", field: attrFieldPrefix + ".groupReturn", dataType: "percentage",
                label: "Group Return",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false
              };

              var attributionBenchmarkReturnCol = {
                id: attrIdPrefix + ".benchmarkReturn", field: attrFieldPrefix + ".benchmarkReturn", dataType: "percentage",
                label: "Benchmark Return",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false
              };

              var attributionLineItemBenchmarkReturnCol;
              if (statSet.composite.attribution.includeLineItemBenchmarkEffect) {
                attributionLineItemBenchmarkReturnCol = {
                  id: attrIdPrefix + ".lineItemBenchmarkReturn", field: attrFieldPrefix + ".lineItemBenchmarkReturn", dataType: "percentage",
                  label: "Line Item Benchmark Return",
                  superHeaders: [tierLabel, attrLabel],
                  hiddenByDefault: false
                };
              }

              var attributionFirstPeriodPortfolioAllocationCol = {
                id: attrIdPrefix + ".firstPeriodPortfolioAllocation",
                field: attrFieldPrefix + ".firstPeriodPortfolioAllocation",
                dataType: "percentage",
                label: "First Period Portfolio Allocation",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false,
                help: {
                  text: "First period allocation percentage adjusted on modified dietz basis for starting cash flows."
                }
              };

              var attributionFirstPeriodPortfolioWeightedStartAmountCol = {
                id: attrIdPrefix + ".firstPeriodPortfolioWeightedStartAmount",
                field: attrFieldPrefix + ".firstPeriodPortfolioWeightedStartAmount",
                dataType: "currency",
                label: "First Period Portfolio Weighted Start Amount",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: true,
                help: {
                  text: "First period allocation amount adjusted on modified dietz basis for starting cash flows."
                }
              };

              var attributionFirstPeriodBenchmarkAllocationCol = {
                id: attrIdPrefix + ".firstPeriodBenchmarkAllocation",
                field: attrFieldPrefix + ".firstPeriodBenchmarkAllocation",
                dataType: "percentage",
                label: "First Period Benchmark Allocation",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false
              };

              var attributionAllocationEffectCol = {
                id: attrIdPrefix + ".allocationEffect", field: attrFieldPrefix + ".allocationEffect", dataType: "percentage",
                label: "Allocation Effect",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false
              };

              var attributionSelectionEffectCol = {
                id: attrIdPrefix + ".selectionEffect", field: attrFieldPrefix + ".selectionEffect", dataType: "percentage",
                label: "Selection Effect",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false
              };

              let attributionInteractionEffectCol;
              if (statSet.composite.attribution.interactionEffect === "Standalone") {
                attributionInteractionEffectCol = {
                  id: attrIdPrefix + ".interactionEffect", field: attrFieldPrefix + ".interactionEffect", dataType: "percentage",
                  label: "Interaction Effect",
                  superHeaders: [tierLabel, attrLabel],
                  hiddenByDefault: false
                };
              }

              var attributionLineItemBenchmarkEffectCol
              if (statSet.composite.attribution.includeLineItemBenchmarkEffect) {
                attributionLineItemBenchmarkEffectCol = {
                  id: attrIdPrefix + ".lineItemBenchmarkEffect", field: attrFieldPrefix + ".lineItemBenchmarkEffect", dataType: "percentage",
                  label: "Line Item Benchmark Effect",
                  superHeaders: [tierLabel, attrLabel],
                  hiddenByDefault: false
                };
              }

              var attributionResidualEffectCol = {
                id: attrIdPrefix + ".residualEffect", field: attrFieldPrefix + ".residualEffect", dataType: "percentage",
                label: "Residual Effect",
                help: otherHelp.attributionResidualEffect,
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: true
              };

              var attributionActiveReturnCol = {
                id: attrIdPrefix + ".activeReturn", field: attrFieldPrefix + ".activeReturn", dataType: "percentage",
                label: "Active Return",
                superHeaders: [tierLabel, attrLabel],
                hiddenByDefault: false
              };

              columns.push(attributionGroupReturnCol);
              columns.push(attributionBenchmarkReturnCol);
              if (statSet.composite.attribution.includeLineItemBenchmarkEffect) {
                columns.push(attributionLineItemBenchmarkReturnCol);
              }
              columns.push(attributionFirstPeriodPortfolioAllocationCol);
              columns.push(attributionFirstPeriodPortfolioWeightedStartAmountCol);
              columns.push(attributionFirstPeriodBenchmarkAllocationCol);
              columns.push(attributionAllocationEffectCol);
              columns.push(attributionSelectionEffectCol);
              if (statSet.composite.attribution.interactionEffect === "Standalone") {
                columns.push(attributionInteractionEffectCol);
              }
              if (statSet.composite.attribution.includeLineItemBenchmarkEffect) {
                columns.push(attributionLineItemBenchmarkEffectCol);
              }
              columns.push(attributionResidualEffectCol);
              columns.push(attributionActiveReturnCol);
            });
          }
        }

        // TX Summaries
        if (statSet.tx) {
          if (statSet.tx.netAmountIn) {
            columns.push({ id: "tx.netAmountIn", field: "txResult.amount.netIn", label: "Net Amount In", dataType: "currency" });
          }
          if (statSet.tx.amountIn) {
            columns.push({ id: "tx.amountIn", field: "txResult.amount.in", label: "Amount In", dataType: "currency" });
          }
          if (statSet.tx.amountOut) {
            columns.push({ id: "tx.amountOut", field: "txResult.amount.out", label: "Amount Out", dataType: "currency" });
          }
          if (statSet.tx.dietzWeightedIn) {
            columns.push({ id: "tx.dietzWeightedIn", field: "txResult.amount.dietzWeightedIn", label: "Dietz WCF In", dataType: "currency" });
          }
          if (statSet.tx.netIncomeIn) {
            columns.push({ id: "tx.netIncomeIn", field: "txResult.amount.netIncomeIn", label: "Net Income In", dataType: "currency" });
          }

          if (statSet.tx.localValues) {
            if (statSet.tx.netAmountIn) {
              columns.push({ id: "tx.netAmountInLocal", field: "txResult.amountLocal.netIn", label: "Net Amount In (Local)", dataType: "currency" });
            }
            if (statSet.tx.amountIn) {
              columns.push({ id: "tx.amountInLocal", field: "txResult.amountLocal.in", label: "Amount In (Local)", dataType: "currency" });
            }
            if (statSet.tx.amountOut) {
              columns.push({ id: "tx.amountOutLocal", field: "txResult.amountLocal.out", label: "Amount Out (Local)", dataType: "currency" });
            }
            if (statSet.tx.dietzWeightedIn) {
              columns.push({ id: "tx.dietzWeightedInLocal", field: "txResult.amountLocal.dietzWeightedIn", label: "Dietz WCF In (Local)", dataType: "currency" });
            }
            if (statSet.tx.netIncomeIn) {
              columns.push({ id: "tx.netIncomeInLocal", field: "txResult.amountLocal.netIncomeIn", label: "Net Income In (Local)", dataType: "currency" });
            }
          }

          if (!_.isEmpty(statSet.tx.customTxGroups)) {
            $(statSet.tx.customTxGroups).each(function (i, customTxGroup: any) {
              var idPrefix = "tx.customGroup." + customTxGroup.code + ".";
              var fieldPrefix = "customTxGroupByCode." + customTxGroup.code + ".";

              var addCustomTxSet = function (txAmountRef, isLocal, labelPrefix?) {
                var setIdPrefix = idPrefix + txAmountRef + ".";
                var setFieldPrefix = fieldPrefix + txAmountRef + ".";
                var labelSuffix = isLocal ? " (Local)" : "";
                var labelPrefixToUse = labelPrefix ? (labelPrefix + " ") : "";

                if (statSet.tx.netAmountIn) {
                  columns.push({
                    id: setIdPrefix + "netAmountIn", field: setFieldPrefix + "netIn", label: labelPrefixToUse + "Net Amount In" + labelSuffix, dataType: "currency",
                    superHeaders: [customTxGroup.label]
                  });
                }
                if (statSet.tx.amountIn) {
                  columns.push({
                    id: setIdPrefix + "amountIn", field: setFieldPrefix + "in", label: labelPrefixToUse + "Amount In" + labelSuffix, dataType: "currency",
                    superHeaders: [customTxGroup.label]
                  });
                }
                if (statSet.tx.amountOut) {
                  columns.push({
                    id: setIdPrefix + "amountOut", field: setFieldPrefix + "out", label: labelPrefixToUse + "Amount Out" + labelSuffix, dataType: "currency",
                    superHeaders: [customTxGroup.label]
                  });
                }
                if (statSet.tx.dietzWeightedIn) {
                  columns.push({
                    id: setIdPrefix + "dietzWeightedIn", field: setFieldPrefix + "dietzWeightedIn", label: labelPrefixToUse + "Dietz WCF In" + labelSuffix, dataType: "currency",
                    superHeaders: [customTxGroup.label]
                  });
                }
                if (statSet.tx.netIncomeIn) {
                  columns.push({
                    id: setIdPrefix + "netIncomeIn", field: setFieldPrefix + "netIncomeIn", label: labelPrefixToUse + "Net Income In" + labelSuffix, dataType: "currency",
                    superHeaders: [customTxGroup.label]
                  });
                }
              };

              addCustomTxSet("amount", false);
              if (statSet.tx.localValues) {
                addCustomTxSet("amountLocal", true);
              }

              if (customTxGroup.startOfDay) {
                addCustomTxSet("startOfDayAmount", false, "Start Of Day");
                if (statSet.tx.localValues) {
                  addCustomTxSet("startOfDayAmountLocal", true, "Start Of Day");
                }
              }
              if (customTxGroup.endOfDay) {
                addCustomTxSet("endOfDayAmount", false, "End Of Day");
                if (statSet.tx.localValues) {
                  addCustomTxSet("endOfDayAmountLocal", true, "End Of Day");
                }
              }
              if (customTxGroup.forceStartOfDay) {
                addCustomTxSet("forcedStartOfDayAmount", false, "Forced Start Of Day");
                if (statSet.tx.localValues) {
                  addCustomTxSet("forcedStartOfDayAmountLocal", true, "Forced Start Of Day");
                }
              }
              if (customTxGroup.forceEndOfDay) {
                addCustomTxSet("forcedEndOfDayAmount", false, "Forced End Of Day");
                if (statSet.tx.localValues) {
                  addCustomTxSet("forcedEndOfDayAmountLocal", true, "Forced End Of Day");
                }
              }
            });
          }
        }

        // Drawdown Stats                
        if (statSet.drawdown) {
          var addDrawdownCols = function (groupField, groupLabel) {
            if (statSet.drawdown.commitment) {
              columns.push({
                id: "drawdown." + groupField + ".commitment",
                field: "drawdownResult." + groupField + ".commitment",
                label: "Commitment",
                dataType: "currency",
                superHeaders: [groupLabel, "Drawdown"]
              });
            }
            if (statSet.drawdown.uncalledCapital) {
              columns.push({
                id: "drawdown." + groupField + ".uncalledCapital",
                field: "drawdownResult." + groupField + ".uncalledCapital",
                label: "Uncalled Capital",
                dataType: "currency",
                superHeaders: [groupLabel, "Drawdown"]
              });
            }
            if (statSet.drawdown.recallable) {
              columns.push({
                id: "drawdown." + groupField + ".recallable",
                field: "drawdownResult." + groupField + ".recallable",
                label: "Recallable",
                dataType: "currency",
                superHeaders: [groupLabel, "Drawdown"]
              });
            }
            if (statSet.drawdown.tvpi) {
              columns.push({
                id: "drawdown." + groupField + ".tvpi",
                field: "drawdownResult." + groupField + ".tvpi",
                label: "TVPI",
                dataType: "number",
                formatHint: { scale: 2 },
                superHeaders: [groupLabel, "Drawdown"]
              });
            }
            if (statSet.drawdown.dpi) {
              columns.push({
                id: "drawdown." + groupField + ".dpi",
                field: "drawdownResult." + groupField + ".dpi",
                label: "DPI",
                dataType: "number",
                formatHint: { scale: 2 },
                superHeaders: [groupLabel, "Drawdown"]
              });
            }
            if (statSet.drawdown.capitalCalledSinceInception) {
              columns.push({
                id: "drawdown." + groupField + ".ccallsSi.all",
                field: "drawdownResult." + groupField + ".ccallsSi.all",
                label: "All",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Capital Called", groupLabel, "Drawdown"]
              });
              columns.push({
                id: "drawdown." + groupField + ".ccallsSi.inv",
                field: "drawdownResult." + groupField + ".ccallsSi.inv",
                label: "For Investment",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Capital Called", groupLabel, "Drawdown"]
              });
              columns.push({
                id: "drawdown." + groupField + ".ccallsSi.fee",
                field: "drawdownResult." + groupField + ".ccallsSi.fee",
                label: "For Fees",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Capital Called", groupLabel, "Drawdown"]
              });
              columns.push({
                id: "drawdown." + groupField + ".ccallsSi.commEx",
                field: "drawdownResult." + groupField + ".ccallsSi.commEx",
                label: "Commitment Excluded",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Capital Called", groupLabel, "Drawdown"]
              });
            }
            if (statSet.drawdown.distributionsSinceInception) {
              columns.push({
                id: "drawdown." + groupField + ".distsSi.all",
                field: "drawdownResult." + groupField + ".distsSi.all",
                label: "All",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Distributions", groupLabel, "Drawdown"]
              });
              columns.push({
                id: "drawdown." + groupField + ".distsSi.roc",
                field: "drawdownResult." + groupField + ".distsSi.roc",
                label: "Return Of Capital",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Distributions", groupLabel, "Drawdown"]
              });
              columns.push({
                id: "drawdown." + groupField + ".distsSi.inc",
                field: "drawdownResult." + groupField + ".distsSi.inc",
                label: "Income",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Distributions", groupLabel, "Drawdown"]
              });
              columns.push({
                id: "drawdown." + groupField + ".distsSi.cap",
                field: "drawdownResult." + groupField + ".distsSi.cap",
                label: "Capital Gains",
                dataType: "currency",
                formatHint: { scale: 2 },
                superHeaders: ["Since Inception", "Distributions", groupLabel, "Drawdown"]
              });
            }
          };

          if (statSet.drawdown.end) {
            addDrawdownCols("end", "Ending");
          }
          if (statSet.drawdown.start) {
            addDrawdownCols("start", "Starting");
          }
          if (statSet.drawdown.local) {
            if (statSet.drawdown.end) {
              addDrawdownCols("endLocal", "Ending (Local)");
            }
            if (statSet.drawdown.start) {
              addDrawdownCols("startLocal", "Starting (Local)");
            }
          }
        }

        columns.push({ id: "errorMessage", field: "errorMessage", label: "Error", dataType: "string", hiddenByDefault: true });

        return columns;
      };




      var propValuesToIncludeHelper = new Solovis.Reports.DataSetHelpers.PropValuesToIncludeHelper({
        dataSourceRefForLineItemsDataSet: { name: "intervalStats", ref: { elementName: "Root", dataSourceName: "intervalStats" } },
        sourceLineItemsProperty: "lineItems",
        sourceModelEnvironmentProperty: null,
        propValuesToIncludeSetParameterName: "propValuesToInclude",
        propValuesDataSourceName: "propValues",

        lineItemToAsOfDayIdCreator: function (parameters, more) {
          return function (lineItem, dateRef) {
            if (lineItem.byIntervalCode) {
              // This is the case of showing all stats on one line, so we use min/max values.
              if (dateRef === "intervalEnd") {
                return lineItem.maxIntervalToDayId;
              }
              else if (dateRef === "intervalStart") {
                return lineItem.minIntervalFromDayId;
              }
            }
            else {
              if (dateRef === "intervalEnd") {
                return lineItem.intervalToDayId;
              }
              else if (dateRef === "intervalStart") {
                return lineItem.intervalFromDayId ? lineItem.intervalFromDayId : 0;
              }
            }

            return null;
          };
        },

        lineItemToResourceIdCreator: function (parameters, more) {
          let lowestLevelResourceIdGetter = function (lineItem, resourceRef) {
            if (resourceRef === "investment") {
              var assetInfo = lineItem.assetInfo;
              if (assetInfo && Solovis.isNotNull(assetInfo.investmentId)) {
                return assetInfo.resourceId;
              }

              if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
                var underlyingInfo = lineItem.compositeInfo.underlyingComponentMetadata;
                if (underlyingInfo && Solovis.isNotNull(underlyingInfo.investmentId)) {
                  return underlyingInfo.resourceId;
                }
              }
            }
            else if (resourceRef === "entityOrHolding") {
              var assetInfo = lineItem.assetInfo;
              if (assetInfo) {
                if (Solovis.isNotNull(assetInfo.entityId)) {
                  return assetInfo.resourceId;
                }
                else if (Solovis.isNotNull(assetInfo.investmentId)) {
                  return assetInfo.holdingResourceId;
                }
              }

              if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
                var underlyingInfo = lineItem.compositeInfo.underlyingComponentMetadata;
                if (underlyingInfo && Solovis.isNotNull(underlyingInfo.entityId)) {
                  return underlyingInfo.resourceId;
                }
                else if (underlyingInfo && Solovis.isNotNull(underlyingInfo.investmentId)) {
                  return underlyingInfo.holdingResourceId;
                }
              }
            }
            else if (resourceRef === "owner") {
              var assetInfo = lineItem.assetInfo;
              if (assetInfo && Solovis.isNotNull(assetInfo.ownerId)) {
                return assetInfo.ownerResourceId;
              }

              if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
                var underlyingInfo = lineItem.compositeInfo.underlyingComponentMetadata;
                if (underlyingInfo && Solovis.isNotNull(underlyingInfo.ownerId)) {
                  return underlyingInfo.ownerResourceId;
                }
              }
            }
            else if (resourceRef === "trueOwner") {
              var assetInfo = lineItem.assetInfo;
              if (assetInfo && Solovis.isNotNull(assetInfo.trueOwnerId)) {
                return assetInfo.trueOwnerResourceId;
              }

              if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
                var underlyingInfo = lineItem.compositeInfo.underlyingComponentMetadata;
                if (underlyingInfo && Solovis.isNotNull(underlyingInfo.trueOwnerId)) {
                  return underlyingInfo.trueOwnerResourceId;
                }
              }
            };
          };

          let finalResourceIdGetter = function (lineItem, resourceRef) {
            if (resourceRef == "investmentHoldingOrEntity") {
              let investmentResourceId = lowestLevelResourceIdGetter(lineItem, "investment");
              let entityOrHoldingResourceId = lowestLevelResourceIdGetter(lineItem, "entityOrHolding");

              let resourceIds = [];
              if (Solovis.isNotNull(investmentResourceId)) {
                resourceIds.push(investmentResourceId);
              }
              if (Solovis.isNotNull(entityOrHoldingResourceId)) {
                resourceIds.push(entityOrHoldingResourceId);
              }
              return resourceIds;
            }
            else if (resourceRef == "investmentHoldingOwnerOrEntity") {
              let investmentResourceId = lowestLevelResourceIdGetter(lineItem, "investment");
              let entityOrHoldingResourceId = lowestLevelResourceIdGetter(lineItem, "entityOrHolding");
              let ownerResourceId = lowestLevelResourceIdGetter(lineItem, "owner");

              let resourceIds = [];
              if (Solovis.isNotNull(investmentResourceId)) {
                resourceIds.push(investmentResourceId);
              }
              if (Solovis.isNotNull(entityOrHoldingResourceId)) {
                resourceIds.push(entityOrHoldingResourceId);
              }
              if (Solovis.isNotNull(ownerResourceId)) {
                resourceIds.push(ownerResourceId);
              }
              return resourceIds;
            }
            else {
              return lowestLevelResourceIdGetter(lineItem, resourceRef);
            }
          };

          return finalResourceIdGetter;
        }
      });

      var dataSourceForPropValues = propValuesToIncludeHelper.createDataSource();

      const getHidePartialPeriodReturns = (): boolean => {
        const custom = (Solovis as any).Custom;
        let reportCode = null;

        const pageViewModel = (window as any).PageViewModel;

        if (pageViewModel && pageViewModel.reportCode) {
          reportCode = pageViewModel.reportCode;
        }

        if (custom && custom.Reports) {
          const customReports = custom.Reports;

          if (customReports.byCode && reportCode) {
            const reportSettings = customReports.byCode[reportCode];
            if (reportSettings && reportSettings.custom && reportSettings.custom.hidePartialPeriodReturns) {
              return reportSettings.custom.hidePartialPeriodReturns;
            }
          }
        }

        return false;
      }

      var report = {
        allowDataPacks: true,

        parameters: [
          {
            name: "assetMode", label: "Asset Mode", parameterType: "choice", initialValue: "Assets",
            settings: {
              options: [
                { label: "Asset List", value: "Assets" },
                { label: "Composite", value: "Composite" }
              ]
            }
          },

          {
            name: "assets", label: "Assets", parameterType: "entityOrInvestment", list: true,
            visible: function () {
              var assetMode = this.getParameterValue("assetMode");
              return assetMode !== assetModes.Composite;
            }
          },
          {
            name: "assetSelectionSet", label: "Asset Selection Set", parameterType: "assetSelectionSet",
            visible: function () {
              var assetMode = this.getParameterValue("assetMode");
              return assetMode !== assetModes.Composite;
            }
          },

          {
            name: "compositeSelectionSet", label: "Composite Selection Set", parameterType: "compositeSelectionSet",
            visible: function () {
              var assetMode = this.getParameterValue("assetMode");
              return assetMode == assetModes.Composite;
            }
          },

          {
            name: "intervalMode", label: "Interval Mode", parameterType: "choice", initialValue: "SingleInterval",
            settings: {
              options: [
                { label: "Single Interval", value: "SingleInterval" },
                { label: "Interval Set", value: "IntervalSet" }
              ]
            }
          },

          {
            name: "from", label: "From", parameterType: "dateIndex",
            visible: function () {
              var intervalMode = this.getParameterValue("intervalMode");
              return intervalMode !== "IntervalSet";
            }
          },
          {
            name: "to", label: "To", parameterType: "dateIndex"
          },

          {
            name: "intervalSet", label: "Interval Set", parameterType: "intervalSet",
            visible: function () {
              var intervalMode = this.getParameterValue("intervalMode");
              return intervalMode === "IntervalSet";
            }
          },

          {
            name: "statSet", label: "Statistics Set", parameterType: "statSet",
            initialValue: {
              basicStats: {
                cumulativeReturn: true,
                hidePartialPeriodReturns: getHidePartialPeriodReturns(),
              }
            }
          },

          { name: "targetCurrency", label: "Target Currency", parameterType: "currency" },
          { name: "sourceData", label: "Source Data", parameterType: "sourceData" },

          {
            name: "showIntervalsAsColumns", label: "Show Intervals As Columns", parameterType: "bool",
            visible: function () {
              var intervalMode = this.getParameterValue("intervalMode");
              return intervalMode === "IntervalSet";
            }
          },


          {
            name: "propValuesToInclude", label: "Property Values To Include", parameterControl: "Solovis.Reports.Parameters.PropValueIncludes.PropValuesToIncludeSetParameterControl",
            settings: {
              resourceOptions: [
                { code: "investment", label: "Investment", resourceTypeId: InvData.ResourceTypeIds.Investment },
                { code: "investmentHoldingOrEntity", label: "Investment/Holding or Entity" },
                { code: "investmentHoldingOwnerOrEntity", label: "Investment/Holding/Owner or Entity" },
                { code: "entityOrHolding", label: "Entity/Holding", resourceTypeId: InvData.ResourceTypeIds.Entity },
                { code: "owner", label: "Owner", resourceTypeId: InvData.ResourceTypeIds.Entity },
                { code: "trueOwner", label: "True Owner", resourceTypeId: InvData.ResourceTypeIds.Entity },
                { code: "manager", label: "Manager", resourceTypeId: InvData.ResourceTypeIds.Manager, indirectRefCode: "entityOrHolding" },
              ],
              dateOptions: [
                { code: "intervalEnd", label: "Interval End" },
                { code: "intervalStart", label: "Interval Start" }
              ],
            }
          },

          {
            name: "otherOptions", label: "Other Options", parameterControl: "Solovis.Reports.Parameters.Stats.StatsOtherOptionsParameterControl"
          }
        ],


        root: {
          elementName: "Root",
          layout: "Vertical",

          children: [
            {
              elementName: "IntervalStatsTable",
              partControl: "Solovis.Reports.Parts.Tables.DataTableControl",

              settings: {
                columns: function () {
                  let self: Solovis.Reports.Parts.Tables.DataTableControl = this;
                  let reportControl = self.reportControl;
                  let statSet = self.parameters.statSet;
                  let intervalStats = self.dataSets.intervalStats;

                  let columns = [];

                  columns.push({ id: "assetResourceId", field: lineResourceIdGetter, label: "Resource Id", dataType: "integer", hiddenByDefault: true });

                  columns.push({ id: "assetEntityId", field: lineEntityIdGetter, label: "Entity Id", dataType: "integer", hiddenByDefault: true });
                  columns.push({ id: "assetInvestmentId", field: lineInvestmentIdGetter, label: "Investment Id", dataType: "integer", hiddenByDefault: true });

                  columns.push({ id: "assetHoldingResourceId", field: lineHoldingResourceIdGetter, label: "Holding Resource Id", dataType: "integer", hiddenByDefault: true });
                  columns.push({ id: "assetHoldingEntityId", field: lineHoldingIdGetter, label: "Holding Entity Id", dataType: "integer", hiddenByDefault: true });

                  columns.push({ id: "assetOwnerResourceId", field: lineOwnerResourceIdGetter, label: "Owner Resource Id", dataType: "integer", hiddenByDefault: true });
                  columns.push({ id: "assetOwnerEntityId", field: lineOwnerIdGetter, label: "Owner Entity Id", dataType: "integer", hiddenByDefault: true });

                  columns.push({ id: "assetTrueOwnerResourceId", field: lineTrueOwnerResourceIdGetter, label: "True Owner Resource Id", dataType: "integer", hiddenByDefault: true });
                  columns.push({ id: "assetTrueOwnerEntityId", field: lineTrueOwnerIdGetter, label: "True Owner Entity Id", dataType: "integer", hiddenByDefault: true });

                  columns.push({
                    id: "assetLabel", field: createAssetLabelFieldGetter(self), label: "Asset", dataType: "string",
                    width: 275,
                    htmlFormatter: assetInfoResourceIdFormatter
                  });

                  if (this.parameters.assetMode === assetModes.Composite) {
                    columns.push({
                      id: "fullLabelPath", field: "compositeInfo.fullLabelPath", label: "Full Label Path", dataType: "string",
                      width: 300
                    });

                    columns.push({
                      id: "isSynthetic", field: isSyntheticComponentLineItem, label: "Is Synthetic", dataType: "bool", hiddenByDefault: true,
                      help: "'Is Synthetic' indicates whether the line item was created by splitting an actual asset into multiple parts (such as when property values change over time)."
                    });

                    columns.push({
                      id: "isGroup", field: "compositeInfo.isGroup", label: "Is Group", dataType: "bool",
                      width: 80, hiddenByDefault: true
                    });
                    columns.push({
                      id: "groupNodeId", field: "compositeInfo.groupNodeId", label: "Group Node Id", dataType: "integer", hiddenByDefault: true
                    });
                    columns.push({
                      id: "parentGroupNodeId", field: "compositeInfo.parentGroupNodeId", label: "Parent Group Node Id", dataType: "integer", hiddenByDefault: true
                    });
                    columns.push({
                      id: "groupNumericOrder", field: "compositeInfo.intSortValue", label: "Group Numeric Order", dataType: "integer", hiddenByDefault: true
                    });
                    columns.push({
                      id: "groupDepth", field: "compositeInfo.depth", label: "Group Depth", dataType: "integer", hiddenByDefault: true
                    });
                    columns.push({
                      id: "composite.tagId", field: "compositeInfo.tagId", label: "Composite Tag Id", dataType: "integer", hiddenByDefault: true
                    });
                    columns.push({
                      id: "composite.tagCode", field: "compositeInfo.tagCode", label: "Composite Tag Code", dataType: "string", hiddenByDefault: true
                    });
                    columns.push({
                      id: "composite.tagSetId", field: "compositeInfo.tagSetId", label: "Composite Tag Set Id", dataType: "integer", hiddenByDefault: true
                    });
                  }

                  // Benchmarks used.
                  columns.push({ id: "basicStatsBenchmark.label", field: "basicStatsBenchmark.label", label: "Basic Stats Benchmark", dataType: "string", hiddenByDefault: true });
                  if (statSet.betaAlpha) {
                    columns.push({ id: "betaAlphaMarket.label", field: "betaAlphaMarket.label", label: "Beta/Alpha Market", dataType: "string", hiddenByDefault: true });
                  }
                  if (statSet.sortinoRatio) {
                    columns.push({ id: "sortinoRatioTargetReturn.label", field: "sortinoRatioTargetReturn.label", label: "Sortino Ratio Target Return", dataType: "string", hiddenByDefault: true });
                  }
                  if (statSet.sharpeRatio) {
                    columns.push({ id: "sharpeRatioBenchmark.label", field: "sharpeRatioBenchmark.label", label: "Sharpe Ratio Benchmark", dataType: "string", hiddenByDefault: true });
                  }
                  if (statSet.informationRatio) {
                    columns.push({ id: "informationRatioBenchmark.label", field: "informationRatioBenchmark.label", label: "Information Ratio Benchmark", dataType: "string", hiddenByDefault: true });
                  }
                  if (statSet.upsideDownsideCaptureRatio) {
                    columns.push({ id: "upsideDownsideCaptureRatioBenchmark.label", field: "upsideDownsideCaptureRatioBenchmark.label", label: "Upside/Downside Capture Ratio Benchmark", dataType: "string", hiddenByDefault: true });
                  }
                  if (statSet.pme) {
                    columns.push({ id: "pmeBenchmark.label", field: "pmeBenchmark.label", label: "PME Benchmark", dataType: "string", hiddenByDefault: true });
                  }

                  columns.push({ id: "assetCurrencyId", field: "assetInfo.currencyId", label: "Currency Id", dataType: "integer", hiddenByDefault: true });
                  columns.push({
                    id: "assetCurrencyCode", field: "assetInfo.currencyCode", label: "Currency", dataType: "string",
                    width: 90, textAlign: "center"
                  });

                  columns.push({ id: "assetLocalCurrencyId", field: "assetInfo.localCurrencyId", label: "Local Currency Id", dataType: "integer", hiddenByDefault: true });
                  columns.push({
                    id: "assetLocalCurrencyCode", field: "assetInfo.localCurrencyCode", label: "Local Currency", dataType: "string",
                    width: 90, textAlign: "center",
                    hiddenByDefault: !(statSet.values && statSet.values.localValues)
                  });

                  if (statSet && statSet.values) {
                    if (statSet.values.contractSize) {
                      columns.push({
                        id: "values.contractSize",
                        field: "assetInfo.contractSize",
                        label: "Contract Size",
                        dataType: "number"
                      });
                    }
                  }

                  if (!this.parameters.showIntervalsAsColumns || this.parameters.intervalMode !== "IntervalSet") {
                    var statColumns = createStatColumns(statSet, this.parameters.intervalMode, intervalStats, reportControl);
                    columns = columns.concat(statColumns);
                  }
                  else {
                    var intervalSetResult = this.dataSets.intervalStats.intervalSetResult;
                    $(intervalSetResult.intervalRequests).each(function (i, intervalRequest: any) {
                      var intervalCode = intervalRequest.code;
                      var intervalInfo = intervalSetResult.codeToIntervalInfoMap[intervalCode];
                      var statColumns = createStatColumnsForInterval(statSet, intervalInfo, self.parameters.intervalMode, intervalStats, reportControl);
                      columns = columns.concat(statColumns);
                    });
                  }

                  if (this.parameters.sourceData && this.parameters.sourceData.useBlendedValues && this.parameters.sourceData.useBookedValues) {
                    columns.push({
                      id: "blendedValuesCutoffDayId", field: "blendedValuesCutoffDayId", label: "Blended Values Cutoff Date", dataType: "dayId", hiddenByDefault: true
                    });
                  }

                  var propColumns = propValuesToIncludeHelper.getDataTableControlColumns({
                    tableControl: this
                  });
                  columns = columns.concat(propColumns);

                  return columns;
                },


                groups: function () {
                  let self: Solovis.Reports.Parts.Tables.DataTableControl = this;

                  let assetLabelGetter = createAssetLabelFieldGetter(self);

                  let groups = [];

                  if (this.parameters.assetMode === assetModes.Composite) {
                    groups.push({
                      id: "all",
                      label: "All",
                      dataRowsForGroups: true,
                      field: "fullParentGroupNodeId",
                      groupValueField: "fullGroupNodeId",
                      htmlFormatter: function (value, valueContext) {
                        var span = $("<span>");
                        var backingRow = valueContext.row._backingRow;
                        if (backingRow) {
                          let assetLabel = assetLabelGetter(backingRow);

                          if (backingRow.assetInfo) {
                            if (!backingRow.fullParentGroupNodeId && backingRow.intervalLabel) {
                              // Include interval label in the "All" group display.
                              span.text(assetLabel + " (" + backingRow.intervalLabel + ")");
                            }
                            else {
                              span.text(assetLabel);
                            }
                          }
                          if (backingRow.errorMessage) {
                            createErrorIndicatorSpan(backingRow.errorMessage).appendTo(span);
                          }
                        }

                        return span;
                      }
                    });
                  }

                  return groups;
                },

                afterUpdateDataView: function () {
                  let tableControl: Solovis.Reports.Parts.Tables.DataTableControl = this;

                  if (tableControl.parameters.assetMode === assetModes.Composite) {
                    // See if we need to group.
                    let shouldRegroupByAll = false;

                    // If we're not currently grouping by all first and someone runs the report with grouping enabled, we should enable grouping by all.
                    var currentGroupByList = tableControl.groupManager.getGroupByList();

                    if (!_.isEmpty(currentGroupByList) && currentGroupByList[0].id !== "all") {
                      shouldRegroupByAll = true;
                    }
                    else {
                      let otherOptions: Solovis.Reports.Parameters.Stats.StatsOtherOptions = tableControl.parameters.otherOptions ? tableControl.parameters.otherOptions : {};

                      if (otherOptions.autoGroupInCompositeMode === "Yes") {
                        shouldRegroupByAll = true;
                      }
                      else if (otherOptions.autoGroupInCompositeMode === "No") {
                        shouldRegroupByAll = false;
                      }
                      else {
                        // Use default for site.
                        let siteReportSettings = tableControl.reportControl.getSiteReportSettings();
                        if (siteReportSettings && siteReportSettings.custom && Solovis.isNotNull(siteReportSettings.custom.autoGroupInCompositeMode)) {
                          shouldRegroupByAll = !!siteReportSettings.custom.autoGroupInCompositeMode;
                        }
                      }
                    }

                    if (shouldRegroupByAll) {
                      if (_.isEmpty(currentGroupByList) || currentGroupByList[0].id !== "all") {
                        this.groupManager.updateGroupByList({ id: "all" });
                      }
                    }
                  }
                },

                cellContextMenuItems: cellContextMenuItems,

                aggregateRow: {
                  display: "inline",
                  groupHeaderColspan: 1
                },

                rowData: "dataSets.intervalStats.lineItems",

                modelEnvironment: { path: "propValues.modelEnvironment" },

                customFunctions: (tableControl: Solovis.Reports.Parts.Tables.DataTableControl) => {
                  let intervalStats = tableControl.dataSets.intervalStats;
                  let intervalStatsHolderMap: IntervalStatsHolderMap = intervalStats.intervalStatsHolderMap;

                  let istats = (intervalCode, fullLabelPath) => {
                    if (intervalCode && fullLabelPath) {
                      return intervalStatsHolderMap.getIntervalStatsHolder(intervalCode, fullLabelPath);
                    }
                  };

                  let tryGetStatsForAssetEntryAtDepth = (statsForEntry, depth, clampToLowestLevel: boolean) => {
                    if (statsForEntry && statsForEntry.compositeInfo) {
                      let compositeInfo = statsForEntry.compositeInfo;
                      if (depth > compositeInfo.depth) {
                        if (clampToLowestLevel) {
                          return statsForEntry;
                        }
                        else {
                          return null;
                        }
                      }
                      else if (depth == compositeInfo.depth) {
                        return statsForEntry;
                      }
                      else {
                        if (compositeInfo.depth == 0) {
                          return null;
                        }
                        else {
                          // Check up the chain.
                          var parentEntry = intervalStatsHolderMap.tryGetStatsByAssetEntryForNodeId(compositeInfo.parentGroupNodeId);
                          return tryGetStatsForAssetEntryAtDepth(parentEntry, depth, false);
                        }
                      }
                    }

                    return null;
                  };

                  let istatsLabelAtLevel = (fullLabelPath, level, clampToLowestLevel: boolean) => {
                    let statsForEntry = intervalStatsHolderMap.tryGetStatsByAssetEntryForFullLabelPath(fullLabelPath);
                    var matchingEntry = tryGetStatsForAssetEntryAtDepth(statsForEntry, level, clampToLowestLevel);
                    if (matchingEntry && matchingEntry.assetInfo) {
                      return matchingEntry.assetInfo.label;
                    }

                    return null;
                  };

                  return {
                    istats: istats,
                    istatsTxGroup: (intervalCode, fullLabelPath, txGroupCode) => {
                      let stats = istats(intervalCode, fullLabelPath);
                      if (stats && stats.txResult && stats.txResult.customTxGroups) {
                        return _.find(stats.txResult.customTxGroups, (g: any) => { return g.groupCode === txGroupCode });
                      }
                    },
                    istatsLabelAtLevel: (fullLabelPath, level) => {
                      return istatsLabelAtLevel(fullLabelPath, level, false);
                    },
                    istatsLabelAtLowestLevel: (fullLabelPath, level) => {
                      return istatsLabelAtLevel(fullLabelPath, level, true);
                    }
                  };
                }
              },

              dataSources: [
                { name: "intervalStats", ref: { elementName: "Root", dataSourceName: "intervalStats" } },
                { name: "propValues", ref: { elementName: "Root", dataSourceName: "propValues" } }
              ]
            }
          ],

          dataSources: [
            Solovis.Reports.StandardDataSources.getSelectionSetDataSource({
              dataSourceName: "selectionSetContents",
              selectionSetParameterName: "assetSelectionSet",
              dayIdParameterName: "to",
              includeAssetsAsParameterList: true,
              includeResourceIds: true
            }),

            dataSourceForIntervalStats,
            dataSourceForPropValues
          ]
        }
      };

      return report;
    }


    public static validateRequiredStatSetOptions(statSet: Solovis.Reports.Parameters.Stats.StatSetOptions) {
      // If any of the following checks fail, we should not submit the request to evaluate Interval Stats.

      if (statSet) {
        if (statSet.sequences) {
          let sequenceCodesSeenSet = {};
          for (let sequence of statSet.sequences) {
            if (!sequence.code) {
              throw "All sequences must have a unique 'Code'. This allows for consistent saving of column customizations.";
            }
            if (sequenceCodesSeenSet[sequence.code]) {
              throw "Duplicate sequence 'Code' found: " + sequence.code;
            }

            sequenceCodesSeenSet[sequence.code] = true;
          }
        }
      }
    }
  }

}